import { jsPDF } from "jspdf";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  User,
  BriefcaseBusiness,
  MapPin,
  Clock3,
  IndianRupee,
  RefreshCw,
  Send,
  AlertCircle,
  Eye,
  X,
  CreditCard,
  CheckCircle2,
  Star,
  ShieldCheck,
  CalendarDays,
  FileText,
  ChevronRight,
  Lock,
  Sparkles,
  SlidersHorizontal,
  RotateCcw,
  ChevronDown,
  Globe,
  Award,
  Trophy,
  FolderGit2,
  Languages,
  Building2,
  GraduationCap,
  UserRound,
  History,
} from "lucide-react";

function SearchTrainers() {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedTrainerId = new URLSearchParams(
    location.search
  ).get("trainerId");

  const TRAINERS_API = "/api/trainers/all";

  const RESUME_API = "/api/trainers/resume";

  // GET /api/trainers/{trainerId}/profile
  const TRAINER_PROFILE_API = "/api/trainers";

  const SUBSCRIPTION_PRODUCTS_API =
    "/api/subscription-products";

  const PURCHASE_API = "/api/subscriptions/purchase";

  const RESUME_ACCESS_STORAGE_KEY = "partner_resume_access";

  // =====================================================
  // TRAINERS
  // =====================================================

  const [search, setSearch] = useState("");
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteTrainer, setInviteTrainer] = useState(null);
  const [partnerJobs, setPartnerJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteJobsLoading, setInviteJobsLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [requestingTrainerId, setRequestingTrainerId] = useState(null);
  const [resumeLoadingId, setResumeLoadingId] = useState(null);
  const [profileDownloadLoadingId, setProfileDownloadLoadingId] =
    useState(null);
  // =====================================================
  // FILTERS
  // =====================================================

  const [locationFilter, setLocationFilter] = useState("");
  const [experienceFilter, setExperienceFilter] = useState("");
  const [skillFilter, setSkillFilter] = useState("");
  const [rateFilter, setRateFilter] = useState("");

  // =====================================================
  // RESUME
  // =====================================================

  const [resumePreviewUrl, setResumePreviewUrl] = useState("");
  const [resumePreviewName, setResumePreviewName] = useState("");
  const [resumePreviewOpen, setResumePreviewOpen] = useState(false);

  // =====================================================
  // SUBSCRIPTION
  // =====================================================

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [subscriptionProducts, setSubscriptionProducts] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState("");
  const [purchaseLoadingId, setPurchaseLoadingId] = useState(null);

  // =====================================================
  // RESUME ACCESS
  // =====================================================

  const [resumeAccess, setResumeAccess] = useState({
    hasPurchasedPlan: false,
    planName: "",
    totalResumeViews: 0,
    remainingResumeViews: 0,
    validity: "",
    productId: null,
  });

  // =====================================================
  // AUTH
  // =====================================================

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getCurrentUser = () => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  };


  const loadResumeAccess = () => {
    try {
      const stored = localStorage.getItem(RESUME_ACCESS_STORAGE_KEY);

      if (!stored) return;

      const parsed = JSON.parse(stored);

      if (parsed) {
        setResumeAccess({
          hasPurchasedPlan: Boolean(parsed.hasPurchasedPlan),
          planName: parsed.planName || "",
          totalResumeViews: Number(parsed.totalResumeViews || 0),
          remainingResumeViews: Number(parsed.remainingResumeViews || 0),
          validity: parsed.validity || "",
          productId: parsed.productId ?? null,
        });
      }
    } catch (err) {
      console.error("Resume access storage error:", err);
    }
  };

  const saveResumeAccess = (access) => {
    try {
      localStorage.setItem(RESUME_ACCESS_STORAGE_KEY, JSON.stringify(access));
    } catch (err) {
      console.error("Unable to save resume access:", err);
    }
  };



  const fetchTrainers = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(TRAINERS_API, {
        method: "GET",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Unable to fetch trainers (${response.status})`);
      }

      const data = await response.json();

      let trainerList = [];

      if (Array.isArray(data)) {
        trainerList = data;
      } else if (Array.isArray(data?.data)) {
        trainerList = data.data;
      } else if (Array.isArray(data?.content)) {
        trainerList = data.content;
      } else if (Array.isArray(data?.trainers)) {
        trainerList = data.trainers;
      }

      const approvedTrainers = trainerList.filter((trainer) => {
        if (!trainer?.status) return true;

        return String(trainer.status).toUpperCase() === "APPROVED";
      });

      setTrainers(approvedTrainers);
    } catch (err) {
      console.error("Trainer fetch error:", err);

      setError(err?.message || "Unable to load trainers.");
    } finally {
      setLoading(false);
    }
  };



  const fetchSubscriptionProducts = async () => {
    try {
      setPlansLoading(true);
      setPlansError("");

      const response = await fetch(SUBSCRIPTION_PRODUCTS_API, {
        method: "GET",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Unable to load subscription plans (${response.status})`
        );
      }

      const data = await response.json();

      let products = [];

      if (Array.isArray(data)) {
        products = data;
      } else if (Array.isArray(data?.data)) {
        products = data.data;
      } else if (Array.isArray(data?.content)) {
        products = data.content;
      } else if (Array.isArray(data?.products)) {
        products = data.products;
      } else if (Array.isArray(data?.subscriptionProducts)) {
        products = data.subscriptionProducts;
      }

      const activeProducts = products.filter((product) => {
        if (product?.active === false) return false;

        if (product?.isActive === false) return false;

        const status = String(product?.status || "").toUpperCase();

        return !["INACTIVE", "DISABLED", "DELETED", "EXPIRED"].includes(
          status
        );
      });

      const partnerProducts = activeProducts.filter((product) => {
        if (product?.forPartner !== undefined) {
          return Boolean(product.forPartner);
        }

        if (product?.partnerOnly !== undefined) {
          return Boolean(product.partnerOnly);
        }

        const roleText = String(
          product?.targetRole ??
          product?.role ??
          product?.userType ??
          product?.audience ??
          ""
        ).toUpperCase();

        if (!roleText) return true;

        return (
          roleText.includes("PARTNER") ||
          roleText.includes("BOTH") ||
          roleText.includes("ALL")
        );
      });

      const resumeProducts = partnerProducts.filter((product) => {
        const text = JSON.stringify(product).toLowerCase();

        return text.includes("resume");
      });

      setSubscriptionProducts(resumeProducts);
    } catch (err) {
      console.error("Subscription products error:", err);

      setPlansError(err?.message || "Unable to load resume access plans.");

      setSubscriptionProducts([]);
    } finally {
      setPlansLoading(false);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    fetchTrainers();
    loadResumeAccess();
  }, []);

  // =====================================================
  // HELPERS
  // =====================================================

  const getSkills = (trainer) => {
    const value =
      trainer?.skills ??
      trainer?.skill ??
      trainer?.technology ??
      trainer?.technologies ??
      "";

    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    if (!value) return [];

    return String(value)
      .split(/[,|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getLocation = (trainer) => {
    const city = trainer?.city || "";
    const state = trainer?.state || "";

    if (city && state) {
      return `${city}, ${state}`;
    }

    return city || state || "Location not available";
  };

  const getExperience = (trainer) => {
    const value =
      trainer?.experience ??
      trainer?.experienceYears ??
      trainer?.yearsOfExperience ??
      trainer?.exp ??
      "";

    if (typeof value === "object" && value !== null) {
      return (
        value.experienceYears ??
        value.totalExperience ??
        value.yearsOfExperience ??
        ""
      );
    }

    return value;
  };



  const getRate = (trainer) => {
    return (
      trainer?.rate ??
      trainer?.hourlyRate ??
      trainer?.trainingRate ??
      trainer?.price ??
      ""
    );
  };

  const getTrainerId = (trainer) => {
    return trainer?.id ?? trainer?.trainerId ?? trainer?.trainer_id;
  };

  const getInitial = (name) => {
    if (!name) return "T";

    return String(name).trim().charAt(0).toUpperCase();
  };

  // =====================================================
  // TRAINER_DETAILS HELPERS
  // (supports camelCase from API JSON or raw snake_case)
  // =====================================================

  const splitList = (value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    if (!value) return [];

    return String(value)
      .split(/[,|\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getDetails = (trainer) => {
    // supports either flat fields on the trainer object
    // or a nested trainerDetails / trainer_details object
    return trainer?.trainerDetails ?? trainer?.trainer_details ?? trainer ?? {};
  };

  const getProfessionalTitle = (trainer) => {
    const d = getDetails(trainer);
    return d?.professionalTitle ?? d?.professional_title ?? "";
  };

  const getProfileSummary = (trainer) => {
    const d = getDetails(trainer);
    return d?.profileSummary ?? d?.profile_summary ?? "";
  };

  const getCurrentOrganization = (trainer) => {
    const d = getDetails(trainer);
    return d?.currentOrganization ?? d?.current_organization ?? "";
  };

  const getCurrentDesignation = (trainer) => {
    const d = getDetails(trainer);
    return d?.currentDesignation ?? d?.current_designation ?? "";
  };

  const getTotalExperience = (trainer) => {
    const d = getDetails(trainer);

    const value =
      d?.totalExperience ??
      d?.total_experience ??
      getExperience(trainer) ??
      "";

    if (typeof value === "object" && value !== null) {
      return (
        value.experienceYears ??
        value.totalExperience ??
        value.yearsOfExperience ??
        ""
      );
    }

    return value;
  };
  const getTrainingExperience = (trainer) => {
    const d = getDetails(trainer);
    return d?.trainingExperience ?? d?.training_experience ?? "";
  };

  const getIndustryExperience = (trainer) => {
    const d = getDetails(trainer);
    return d?.industryExperience ?? d?.industry_experience ?? "";
  };

  const getTechnicalSkills = (trainer) =>
    splitList(
      getDetails(trainer)?.technicalSkills ??
      getDetails(trainer)?.technical_skills
    );

  const getProgrammingLanguages = (trainer) =>
    splitList(
      getDetails(trainer)?.programmingLanguages ??
      getDetails(trainer)?.programming_languages
    );

  const getFrameworks = (trainer) => splitList(getDetails(trainer)?.frameworks);

  const getDatabaseTechnologies = (trainer) =>
    splitList(
      getDetails(trainer)?.databaseTechnologies ??
      getDetails(trainer)?.database_technologies
    );

  const getCloudTools = (trainer) =>
    splitList(
      getDetails(trainer)?.cloudTools ?? getDetails(trainer)?.cloud_tools
    );

  const getDevopsTools = (trainer) =>
    splitList(
      getDetails(trainer)?.devopsTools ?? getDetails(trainer)?.devops_tools
    );

  const getCertifications = (trainer) =>
    splitList(getDetails(trainer)?.certifications);

  const getAchievements = (trainer) =>
    splitList(getDetails(trainer)?.achievements);

  const getNotableProjects = (trainer) =>
    splitList(
      getDetails(trainer)?.notableProjects ??
      getDetails(trainer)?.notable_projects
    );

  const getLinkedinUrl = (trainer) => {
    const d = getDetails(trainer);
    return d?.linkedinUrl ?? d?.linkedin_url ?? "";
  };

  const getGithubUrl = (trainer) => {
    const d = getDetails(trainer);
    return d?.githubUrl ?? d?.github_url ?? "";
  };

  const getPortfolioUrl = (trainer) => {
    const d = getDetails(trainer);
    return d?.portfolioUrl ?? d?.portfolio_url ?? "";
  };

  const getLanguagesKnown = (trainer) =>
    splitList(
      getDetails(trainer)?.languagesKnown ??
      getDetails(trainer)?.languages_known
    );

  const getTeachingMode = (trainer) => {
    const d = getDetails(trainer);
    return d?.teachingMode ?? d?.teaching_mode ?? "";
  };

  const getPreferredLocations = (trainer) =>
    splitList(
      getDetails(trainer)?.preferredLocations ??
      getDetails(trainer)?.preferred_locations
    );

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCreatedAt = (trainer) => {
    const d = getDetails(trainer);
    return formatDate(d?.createdAt ?? d?.created_at);
  };

  const getUpdatedAt = (trainer) => {
    const d = getDetails(trainer);
    return formatDate(d?.updatedAt ?? d?.updated_at);
  };

  const getProfilePictureUrl = (trainer) => {
    const d = getDetails(trainer);

    const pic = d?.profilePicture ?? d?.profile_picture;

    if (!pic) return "";

    if (typeof pic === "string" && pic.startsWith("data:")) {
      return pic;
    }

    const type =
      d?.profilePictureType ?? d?.profile_picture_type ?? "image/jpeg";

    return `data:${type};base64,${pic}`;
  };
  const getDisplaySkills = (trainer) => {
    const combined = [
      ...getTechnicalSkills(trainer),
      ...getProgrammingLanguages(trainer),
      ...getFrameworks(trainer),
    ];

    if (combined.length > 0) {
      return [...new Set(combined)];
    }

    return getSkills(trainer);
  };

  // devops), falling back to the legacy trainer.skills field.
  const getAllSkillsFull = (trainer) => {
    const combined = [
      ...getTechnicalSkills(trainer),
      ...getProgrammingLanguages(trainer),
      ...getFrameworks(trainer),
      ...getDatabaseTechnologies(trainer),
      ...getCloudTools(trainer),
      ...getDevopsTools(trainer),
    ];

    if (combined.length > 0) {
      return [...new Set(combined)];
    }

    return getSkills(trainer);
  };

  // =====================================================
  // UNIQUE FILTER OPTIONS
  // =====================================================

  const locations = useMemo(() => {
    return [
      ...new Set(
        trainers
          .map((trainer) => getLocation(trainer))
          .filter((location) => location !== "Location not available")
      ),
    ].sort();
  }, [trainers]);

  const allSkills = useMemo(() => {
    return [
      ...new Set(trainers.flatMap((trainer) => getDisplaySkills(trainer))),
    ].sort();
  }, [trainers]);

  // =====================================================
  // FILTER TRAINERS
  // =====================================================

  const filteredTrainers = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return trainers.filter((trainer) => {
      const trainerName = trainer?.name || trainer?.fullName || "";

      const location = getLocation(trainer);
      const skills = getDisplaySkills(trainer);
      const experience = Number(getExperience(trainer)) || 0;
      const rate = Number(getRate(trainer)) || 0;

      const searchableText = [
        trainerName,
        location,
        trainer?.city,
        trainer?.state,
        skills.join(" "),
        getProfessionalTitle(trainer),
        getCurrentOrganization(trainer),
        getCurrentDesignation(trainer),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      // SEARCH
      if (searchValue && !searchableText.includes(searchValue)) {
        return false;
      }

      // LOCATION
      if (locationFilter && location !== locationFilter) {
        return false;
      }

      // SKILL
      if (
        skillFilter &&
        !skills.some(
          (skill) => skill.toLowerCase() === skillFilter.toLowerCase()
        )
      ) {
        return false;
      }

      // EXPERIENCE
      if (experienceFilter) {
        if (experienceFilter === "0-2" && !(experience <= 2)) {
          return false;
        }

        if (experienceFilter === "3-5" && !(experience >= 3 && experience <= 5)) {
          return false;
        }

        if (
          experienceFilter === "6-10" &&
          !(experience >= 6 && experience <= 10)
        ) {
          return false;
        }

        if (experienceFilter === "10+" && !(experience > 10)) {
          return false;
        }
      }

      // RATE
      if (rateFilter) {
        if (rateFilter === "under-1000" && !(rate > 0 && rate < 1000)) {
          return false;
        }

        if (rateFilter === "1000-2500" && !(rate >= 1000 && rate <= 2500)) {
          return false;
        }

        if (rateFilter === "2500+" && !(rate > 2500)) {
          return false;
        }
      }

      return true;
    });
  }, [
    trainers,
    search,
    locationFilter,
    skillFilter,
    experienceFilter,
    rateFilter,
  ]);

  // =====================================================
  // CLEAR FILTERS
  // =====================================================

  const clearFilters = () => {
    setSearch("");
    setLocationFilter("");
    setExperienceFilter("");
    setSkillFilter("");
    setRateFilter("");
  };

  const hasActiveFilters =
    search || locationFilter || experienceFilter || skillFilter || rateFilter;

  // =====================================================
  // RESUME ACCESS
  // =====================================================

  const hasPurchasedResumeAccess =
    resumeAccess.hasPurchasedPlan && resumeAccess.remainingResumeViews > 0;

  // =====================================================
  // FULL PROFILE MODAL (fetched from /trainers/{id}/profile)
  // =====================================================

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalTrainer, setProfileModalTrainer] = useState(null);
  const [profileTarget, setProfileTarget] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");

  const showProfileModal = profileModalOpen;


  
const openProfileModal = async (trainer) => {
  const trainerId = getTrainerId(trainer);

  if (!trainerId) {
    alert("Trainer ID is missing.");
    return;
  }

  let user;

  try {
    user = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    user = null;
  }

  if (!user?.id) {
    alert("Please log in first.");
    return;
  }

  // Read the role saved for the logged-in user.
  const role = String(
    user.role ??
    user.userRole ??
    user.userType ??
    user.type ??
    ""
  ).toUpperCase();

  const isAdmin = role === "ADMIN";

  setProfileTarget(trainer);
  setProfileModalOpen(true);
  setProfileModalTrainer(null);
  setProfileError("");
  setProfileLoading(true);

  try {
    // Only Partners consume profile-view quota.
    if (!isAdmin) {
      const viewResponse = await fetch(
        `/api/trainers/${trainerId}/view?partnerUserId=${encodeURIComponent(user.id)}`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      if (!viewResponse.ok) {
        const responseText = await viewResponse.text();

        let message = `Unable to record profile view (${viewResponse.status})`;

        try {
          const errorData = JSON.parse(responseText);
          message =
            errorData.message ||
            errorData.error ||
            message;
        } catch {
          if (responseText) {
            message = responseText;
          }
        }

        throw new Error(message);
      }
    }

    // Admin and Partner both load the trainer profile.
    const response = await fetch(
      `${TRAINER_PROFILE_API}/${trainerId}/profile`,
      {
        method: "GET",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const responseText = await response.text();

      throw new Error(
        `Unable to load trainer profile (${response.status}): ${responseText}`
      );
    }

    const data = await response.json();
    const profileData = data?.data ?? data?.trainer ?? data;

    setProfileModalTrainer({
      ...trainer,
      ...profileData,
    });

    // Notify usage components only for Partner views.
    if (!isAdmin) {
      window.dispatchEvent(
        new CustomEvent("partner-usage-updated")
      );
    }
  } catch (err) {
    console.error("Trainer profile view error:", err);
    setProfileError(
      err?.message || "Unable to load trainer profile."
    );
  } finally {
    setProfileLoading(false);
  }
};

  useEffect(() => {
    if (!selectedTrainerId || loading || trainers.length === 0) {
      return;
    }

    const selectedTrainer = trainers.find((trainer) => {
      const id = trainer.id ?? trainer.trainerId ?? trainer.trainer_id;
      return String(id) === String(selectedTrainerId);
    });

    if (selectedTrainer) {
      openProfileModal(selectedTrainer);
    } else {
      console.warn(
        "Trainer not found for ID:",
        selectedTrainerId
      );
    }
  }, [selectedTrainerId, loading, trainers]);

  const closeProfileModal = () => {
    setProfileModalOpen(false);
    setProfileModalTrainer(null);
    setProfileTarget(null);
    setProfileError("");
    setProfileLoading(false);

    // Remove trainerId from the URL so the modal does not reopen.
    navigate("/partner/search-trainers", {
      replace: true,
    });
  };

  // =====================================================
  // VIEW RESUME
  // =====================================================

  const handleViewResume = async (trainer) => {
    const trainerId = getTrainerId(trainer);

    if (!trainerId) {
      alert("Trainer ID is missing.");
      return;
    }

    try {
      const email = trainer?.email;

      if (!email) {
        alert("Trainer email is missing.");
        return;
      }

      // Open a blank tab immediately to avoid popup blockers.
      const resumeWindow = window.open("about:blank", "_blank");

      if (!resumeWindow) {
        alert("Please allow pop-ups to view the resume.");
        return;
      }

      resumeWindow.document.title = "Trainer Resume";
      resumeWindow.document.body.innerHTML =
        "<p style='font-family:Arial;padding:20px'>Loading resume...</p>";

      const response = await fetch(
        `/api/trainers/${trainerId}/resume-file?email=${encodeURIComponent(email)}`,
        {
          method: "GET",
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        resumeWindow.close();

        if (response.status === 404) {
          throw new Error(
            "No registration resume was found for this trainer."
          );
        }

        throw new Error(
          `Unable to open resume (${response.status}).`
        );
      }

      const blob = await response.blob();

      if (!blob.size) {
        resumeWindow.close();
        throw new Error("The uploaded resume is empty.");
      }

      // Use the response's actual content type.
      const contentType =
        response.headers.get("Content-Type") ||
        blob.type ||
        "application/pdf";

      const resumeBlob = new Blob([blob], {
        type: contentType,
      });

      const resumeUrl = URL.createObjectURL(resumeBlob);

      // PDF and browser-supported document types open in the new tab.
      resumeWindow.location.href = resumeUrl;

      // Keep the URL alive while the document is being viewed.
      setTimeout(() => {
        URL.revokeObjectURL(resumeUrl);
      }, 60000);
    } catch (error) {
      console.error("View resume error:", error);
      alert(error.message || "Unable to view the resume.");
    }
  };
  // =====================================================
  // DOWNLOAD TRAINER PROFILE
  // POST /api/trainers/{id}/profile-download
  // =====================================================
  //profile download//resume


  const handleDownloadProfile = async (trainer) => {
    const trainerId = getTrainerId(trainer);
    const user = getCurrentUser();

    if (!trainerId) {
      alert("Trainer ID is missing.");
      return;
    }

    if (!user?.id) {
      alert("Please log in as a partner.");
      return;
    }

    try {
      setProfileDownloadLoadingId(trainerId);

      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };

      // 1. Fetch the basic trainer record.
      const trainerResponse = await fetch(
        `${TRAINER_PROFILE_API}/${trainerId}`,
        { method: "GET", headers }
      );

      if (!trainerResponse.ok) {
        throw new Error(
          `Unable to fetch trainer details (${trainerResponse.status}).`
        );
      }

      const trainerData = await trainerResponse.json();

      const fullTrainer =
        trainerData?.data ??
        trainerData?.trainer ??
        trainerData;

      if (!fullTrainer || typeof fullTrainer !== "object") {
        throw new Error("Trainer data was not returned.");
      }

      // 2. Fetch professional details, education and experience.
      const profileResponse = await fetch(
        `${TRAINER_PROFILE_API}/${trainerId}/profile`,
        { method: "GET", headers }
      );

      if (!profileResponse.ok) {
        throw new Error(
          `Unable to fetch complete profile (${profileResponse.status}).`
        );
      }

      const profileResult = await profileResponse.json();

      const profileData =
        profileResult?.data ??
        profileResult?.profile ??
        profileResult;

      const details = profileData?.details || {};

      const educationList = Array.isArray(profileData?.education)
        ? profileData.education
        : [];

      const experienceList = Array.isArray(profileData?.experience)
        ? profileData.experience
        : [];

      // Support camelCase and snake_case field names.
      const getField = (object, camel, snake) =>
        object?.[camel] ?? object?.[snake] ?? "";

      const hasValue = (value) =>
        value !== undefined &&
        value !== null &&
        String(value).trim() !== "";

      const displayValue = (value) => {
        if (Array.isArray(value)) {
          return value.filter(hasValue).join(", ");
        }

        if (typeof value === "boolean") {
          return value ? "Yes" : "No";
        }

        return hasValue(value) ? String(value) : "";
      };

      // 3. Create the PDF.
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 42;
      const contentWidth = pageWidth - margin * 2;
      const bottomLimit = pageHeight - margin;
      let y = 42;

      const trainerName =
        fullTrainer.name ||
        fullTrainer.fullName ||
        "Trainer";

      const safeFileName =
        String(trainerName)
          .replace(/[^\w.-]+/g, "-")
          .replace(/^-+|-+$/g, "") || "Trainer";

      const addPageIfNeeded = (height) => {
        if (y + height > bottomLimit) {
          doc.addPage();
          y = margin;
        }
      };

      const addParagraph = (value, options = {}) => {
        const text = displayValue(value);
        if (!text) return;

        const {
          fontSize = 10,
          bold = false,
          indent = 0,
          color = [48, 42, 58],
          gapAfter = 7,
        } = options;

        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(fontSize);
        doc.setTextColor(...color);

        const lines = doc.splitTextToSize(
          text,
          contentWidth - indent
        );

        const lineHeight = fontSize * 1.45;
        const blockHeight = lines.length * lineHeight + gapAfter;

        addPageIfNeeded(blockHeight);

        doc.text(lines, margin + indent, y);
        y += blockHeight;
      };

      const addSection = (title) => {
        addPageIfNeeded(42);

        y += 5;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(62, 15, 141);
        doc.text(title, margin, y);

        y += 7;

        doc.setDrawColor(228, 224, 234);
        doc.line(margin, y, pageWidth - margin, y);

        y += 17;
      };

      const addDetail = (label, value) => {
        const text = displayValue(value);
        if (!text) return;

        const labelWidth = 128;
        const valueWidth = contentWidth - labelWidth;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);

        const lines = doc.splitTextToSize(text, valueWidth);
        const lineHeight = 13;
        const blockHeight = Math.max(
          lineHeight,
          lines.length * lineHeight
        );

        addPageIfNeeded(blockHeight + 8);

        doc.setFont("helvetica", "bold");
        doc.setTextColor(90, 82, 104);
        doc.text(String(label), margin, y);

        doc.setFont("helvetica", "normal");
        doc.setTextColor(48, 42, 58);
        doc.text(lines, margin + labelWidth, y);

        y += blockHeight + 8;
      };

      const addList = (title, value) => {
        const items = Array.isArray(value)
          ? value
          : String(value || "").split(/[,;\n]+/);

        const validItems = items
          .filter(hasValue)
          .map((item) => String(item).trim());

        if (!validItems.length) return;

        addSection(title);

        validItems.forEach((item) => {
          addParagraph(`• ${item}`, {
            fontSize: 9.5,
            indent: 5,
            gapAfter: 4,
          });
        });
      };

      const formatDate = (value) => {
        if (!value) return "";

        // API date fields are expected as ISO date strings.
        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
          return String(value);
        }

        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        });
      };

      // 4. Branded header.
      doc.setFillColor(62, 15, 141);
      doc.rect(0, 0, pageWidth, 94, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(228, 218, 114);
      doc.text("CERTED | TRAINER PROFILE", margin, 29);

      doc.setFontSize(21);
      doc.setTextColor(255, 255, 255);

      const nameLines = doc.splitTextToSize(
        trainerName,
        contentWidth
      );

      doc.text(nameLines.slice(0, 2), margin, 57);
      y = 119;

      addParagraph(
        getField(details, "professionalTitle", "professional_title") ||
        fullTrainer.professionalType,
        {
          fontSize: 12,
          bold: true,
          color: [149, 100, 221],
        }
      );

      // 5. Basic trainer information.
      addSection("Basic Trainer Information");

      [
        ["Full Name", fullTrainer.name || fullTrainer.fullName],
        ["Email", fullTrainer.email],
        ["Secondary Email", fullTrainer.secondaryEmail],
        ["Phone", fullTrainer.phone],
        ["Secondary Phone", fullTrainer.secondaryPhone],
        ["Age", fullTrainer.age],
        ["Professional Type", fullTrainer.professionalType],
        ["Experience", hasValue(fullTrainer.experience)
          ? `${fullTrainer.experience} year(s)`
          : ""],
        ["Skills", fullTrainer.skills],
        ["Training Rate", hasValue(fullTrainer.rate)
          ? `INR ${fullTrainer.rate}`
          : ""],
        ["Status", fullTrainer.status],
        ["Source", fullTrainer.source],
        ["Remark", fullTrainer.remark],
      ].forEach(([label, value]) => addDetail(label, value));

      // 6. Professional profile.
      addSection("Professional Profile");

      [
        ["Professional Title",
          getField(details, "professionalTitle", "professional_title")],
        ["Profile Summary",
          getField(details, "profileSummary", "profile_summary")],
        ["Current Organization",
          getField(details, "currentOrganization", "current_organization")],
        ["Current Designation",
          getField(details, "currentDesignation", "current_designation")],
        ["Total Experience (Years)",
          getField(details, "totalExperience", "total_experience")],
        ["Training Experience (Years)",
          getField(details, "trainingExperience", "training_experience")],
        ["Industry Experience (Years)",
          getField(details, "industryExperience", "industry_experience")],
        ["Teaching Mode",
          getField(details, "teachingMode", "teaching_mode")],
      ].forEach(([label, value]) => addDetail(label, value));

      // 7. Teaching details from the basic trainer record.
      addSection("Teaching Details");

      [
        ["Subjects", fullTrainer.subjects],
        ["Classes", fullTrainer.classes],
        ["Board", fullTrainer.board],
        ["Exam Specialization", fullTrainer.examSpecialization],
      ].forEach(([label, value]) => addDetail(label, value));

      // 8. Technical skills.
      addSection("Technical Skills");

      [
        ["Technical Skills",
          getField(details, "technicalSkills", "technical_skills")],
        ["Programming Languages",
          getField(details, "programmingLanguages", "programming_languages")],
        ["Frameworks", details.frameworks],
        ["Database Technologies",
          getField(details, "databaseTechnologies", "database_technologies")],
        ["Cloud Tools",
          getField(details, "cloudTools", "cloud_tools")],
        ["DevOps Tools",
          getField(details, "devopsTools", "devops_tools")],
      ].forEach(([label, value]) => addDetail(label, value));

      // 9. Education: include every returned education record.
      addSection("Education");

      if (educationList.length === 0) {
        addParagraph("No education records available.", {
          fontSize: 9.5,
        });
      } else {
        educationList.forEach((education, index) => {
          addParagraph(
            education.degree || `Education ${index + 1}`,
            { fontSize: 11, bold: true }
          );

          const duration = [
            education.startYear,
            education.endYear,
          ].filter((year) => year !== null && year !== undefined).join(" - ");

          [
            ["Specialization", education.specialization],
            ["University", education.university],
            ["College / Institution", education.college],
            ["Duration", duration],
            ["Education Type", education.educationType],
            ["CGPA", education.cgpa],
            ["Percentage", education.percentage],
            ["Description", education.description],
          ].forEach(([label, value]) => addDetail(label, value));

          y += 4;
        });
      }

      // 10. Professional experience: include every returned record.
      addSection("Professional Experience");

      if (experienceList.length === 0) {
        addParagraph("No professional experience records available.", {
          fontSize: 9.5,
        });
      } else {
        experienceList.forEach((experience, index) => {
          addParagraph(
            experience.designation ||
            experience.companyName ||
            `Experience ${index + 1}`,
            { fontSize: 11, bold: true }
          );

          const duration = [
            formatDate(experience.startDate),
            experience.isCurrent
              ? "Present"
              : formatDate(experience.endDate),
          ].filter(Boolean).join(" - ");

          [
            ["Company", experience.companyName],
            ["Designation", experience.designation],
            ["Employment Type", experience.employmentType],
            ["Location", experience.location],
            ["Duration", duration],
            ["Experience (Years)", experience.experienceYears],
            ["Job Description", experience.jobDescription],
            ["Responsibilities", experience.responsibilities],
            ["Technologies Used", experience.technologiesUsed],
          ].forEach(([label, value]) => addDetail(label, value));

          y += 4;
        });
      }

      // 11. Certifications, achievements and projects.
      addList(
        "Certifications",
        getField(details, "certifications", "certifications")
      );

      addList(
        "Achievements",
        getField(details, "achievements", "achievements")
      );

      addList(
        "Notable Projects",
        getField(details, "notableProjects", "notable_projects")
      );

      // 12. Location and additional information.
      addSection("Location & Additional Information");

      [
        ["City", fullTrainer.city],
        ["State", fullTrainer.state],
        ["Pincode", fullTrainer.pincode],
        ["Languages Known",
          getField(details, "languagesKnown", "languages_known")],
        ["Preferred Locations",
          getField(details, "preferredLocations", "preferred_locations")],
      ].forEach(([label, value]) => addDetail(label, value));

      // 13. Professional links.
      addSection("Professional Links");

      [
        ["LinkedIn", getField(details, "linkedinUrl", "linkedin_url")],
        ["GitHub", getField(details, "githubUrl", "github_url")],
        ["Portfolio", getField(details, "portfolioUrl", "portfolio_url")],
      ].forEach(([label, value]) => addDetail(label, value));

      // 14. Resume metadata only. Do not put binary data into the PDF.
      addSection("Resume Information");

      [
        ["Resume File Name", fullTrainer.resumeName],
        ["Resume File Type",
          fullTrainer.resumeType ||
          getField(details, "resumeType", "resume_type")],
      ].forEach(([label, value]) => addDetail(label, value));

      // 15. Page numbers.
      const totalPages = doc.getNumberOfPages();

      for (let page = 1; page <= totalPages; page++) {
        doc.setPage(page);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(130, 123, 140);

        doc.text(
          `CertED Trainer Profile | Page ${page} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 18,
          { align: "center" }
        );
      }

      // 16. Validate PDF before recording the download.
      const pdfBlob = doc.output("blob");

      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error("Unable to generate the Profile PDF.");
      }

      // 17. Preserve the existing backend quota validation.
      const quotaResponse = await fetch(
        `${TRAINER_PROFILE_API}/${trainerId}/profile-download?partnerUserId=${encodeURIComponent(user.id)}`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      let responseData = {};

      try {
        responseData = await quotaResponse.json();
      } catch {
        responseData = {};
      }

      if (!quotaResponse.ok) {
        if (
          quotaResponse.status === 403 &&
          responseData.code === "QUOTA_EXCEEDED"
        ) {
          setShowSubscriptionModal(true);
          return;
        }

        throw new Error(
          responseData.message ||
          `Profile download failed (${quotaResponse.status}).`
        );
      }

      // 18. Save only after backend approval.
      doc.save(`${safeFileName}-Profile.pdf`);

      if (responseData.quotaConsumed) {
        window.dispatchEvent(
          new CustomEvent("partner-usage-updated")
        );
      }
    } catch (err) {
      console.error("Profile PDF download error:", err);
      alert(err?.message || "Unable to download the Profile PDF.");
    } finally {
      setProfileDownloadLoadingId(null);
    }
  };


  // =====================================================
  // CLOSE RESUME
  // =====================================================

  const closeResumePreview = () => {
    if (resumePreviewUrl) {
      URL.revokeObjectURL(resumePreviewUrl);
    }

    setResumePreviewUrl("");
    setResumePreviewName("");
    setResumePreviewOpen(false);
  };

  // =====================================================
  // SEND REQUEST
  // =====================================================

  const handleSendRequest = async (trainer) => {
    const trainerId = getTrainerId(trainer);

    if (!trainerId) {
      alert("Trainer ID is missing.");
      return;
    }

    let user;

    try {
      user = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      user = null;
    }

    if (!user?.id) {
      alert("Please log in again as a partner.");
      return;
    }

    setInviteTrainer(trainer);
    setInviteModalOpen(true);
    setPartnerJobs([]);
    setSelectedJobId("");
    setInviteMessage("");
    setInviteError("");
    setInviteJobsLoading(true);

    try {
      const response = await fetch(
        `/api/jobs/partner/${encodeURIComponent(user.id)}`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Unable to load your jobs.");
      }

      // Support either a direct array or a wrapped response.
      const jobs = Array.isArray(data)
        ? data
        : Array.isArray(data?.jobs)
          ? data.jobs
          : [];

      // Only show approved jobs.
      const approvedJobs = jobs.filter(
        (job) => String(job.status || "").toUpperCase() === "APPROVED"
      );

      setPartnerJobs(approvedJobs);

      if (approvedJobs.length === 0) {
        setInviteError(
          "You have no approved jobs available for invitations."
        );
      }
    } catch (err) {
      console.error("Unable to load partner jobs:", err);
      setInviteError(err.message || "Unable to load your jobs.");
    } finally {
      setInviteJobsLoading(false);
    }
  };
  const submitTrainerInvite = async (e) => {
    e.preventDefault();

    const trainerId = getTrainerId(inviteTrainer);

    let user;

    try {
      user = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      user = null;
    }

    if (!trainerId || !user?.id || !selectedJobId) {
      setInviteError("Please select a job before sending the invitation.");
      return;
    }

    setRequestingTrainerId(trainerId);
    setInviteError("");

    try {
      const params = new URLSearchParams({
        partnerUserId: String(user.id),
        jobId: String(selectedJobId),
        message: inviteMessage.trim(),
      });

      const response = await fetch(
        `/api/trainers/${trainerId}/invite?${params.toString()}`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
          },
        }
      );

      let data = {};

      try {
        data = await response.json();
      } catch {
        // Handle responses without JSON.
      }

      if (!response.ok) {
        if (
          response.status === 403 &&
          data?.code === "QUOTA_EXCEEDED"
        ) {
          setInviteModalOpen(false);
          setShowSubscriptionModal(true);
          return;
        }

        throw new Error(data?.message || "Unable to send invitation.");
      }

      window.dispatchEvent(new CustomEvent("partner-usage-updated"));

      setInviteModalOpen(false);
      setInviteTrainer(null);

      alert("Job invitation sent successfully!");
    } catch (err) {
      console.error("Invitation error:", err);
      setInviteError(err.message || "Unable to send invitation.");
    } finally {
      setRequestingTrainerId(null);
    }
  };

  // =====================================================
  // PRODUCT HELPERS
  // =====================================================

  const getProductName = (product) =>
    product?.productName ||
    product?.name ||
    product?.title ||
    "Resume Access Plan";

  const getProductValidity = (product) =>
    product?.validity ||
    product?.duration ||
    product?.billingCycle ||
    product?.planDuration ||
    "";

  const getResumeLimit = (product) =>
    product?.resumeLimit ??
    product?.resumeViews ??
    product?.maxResumeViews ??
    product?.resumeAccessLimit ??
    product?.trainerResumeLimit ??
    product?.numberOfResumes ??
    product?.resumeCount ??
    null;

  const getProductDescription = (product) =>
    product?.description ||
    product?.shortDescription ||
    product?.productDescription ||
    "Access approved trainer resumes according to your selected plan.";

  const getProductFeatures = (product) => {
    const features =
      product?.features ?? product?.featureList ?? product?.benefits ?? [];

    if (Array.isArray(features)) {
      return features
        .map((feature) =>
          typeof feature === "string"
            ? feature
            : feature?.name || feature?.title || feature?.description || ""
        )
        .filter(Boolean);
    }

    if (typeof features === "string") {
      return features
        .split(/[,|\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null || price === "") {
      return "Contact Us";
    }

    const numericPrice = Number(price);

    return Number.isFinite(numericPrice)
      ? numericPrice.toLocaleString("en-IN", {
        maximumFractionDigits: 0,
      })
      : String(price);
  };


  const handlePurchasePlan = async (product) => {
    const productId =
      product?.id ?? product?.productId ?? product?.subscriptionProductId;

    if (!productId) {
      alert("Subscription product ID is missing.");
      return;
    }

    try {
      setPurchaseLoadingId(productId);

      const user = getCurrentUser();

      if (!user?.id) {
        throw new Error("Partner user ID is missing.");
      }

      const response = await fetch(PURCHASE_API, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productId,
          userId: user.id,
          role: user?.role || "PARTNER",
        }),
      });

      let data = null;

      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          data?.message || `Unable to purchase plan (${response.status})`
        );
      }

      const productLimit = getResumeLimit(product);

      const apiLimit =
        data?.resumeLimit ??
        data?.resumeViews ??
        data?.remainingResumeViews ??
        data?.subscription?.resumeLimit ??
        data?.subscription?.resumeViews ??
        productLimit;

      const finalLimit = Number(apiLimit) || 0;

      const updatedAccess = {
        hasPurchasedPlan: true,
        planName: data?.productName || data?.planName || getProductName(product),
        totalResumeViews: finalLimit,
        remainingResumeViews: finalLimit,
        validity:
          data?.validity || data?.duration || getProductValidity(product),
        productId,
      };

      setResumeAccess(updatedAccess);
      saveResumeAccess(updatedAccess);

      setShowPlansModal(false);
      setShowSubscriptionModal(false);

      alert(`${getProductName(product)} purchased successfully.`);
    } catch (err) {
      console.error("Purchase error:", err);

      alert(err?.message || "Unable to process purchase.");
    } finally {
      setPurchaseLoadingId(null);
    }
  };

  const openPlans = () => {
    setShowSubscriptionModal(false);
    setShowPlansModal(true);
    fetchSubscriptionProducts();
  };


  return (
    <>
      <div className="srt-certed-trainer-page">


        <div className="srt-certed-header">
          <div>
            <div className="srt-certed-eyebrow">
              <BriefcaseBusiness size={15} />
              CERTED TRAINER MARKETPLACE
            </div>

            <h1>Search Trainers</h1>

            <p>
              Discover approved trainers for your academic and corporate
              training requirements.
            </p>
          </div>

          <div className="srt-header-stats">
            {resumeAccess.hasPurchasedPlan && (
              <div className="srt-header-stat srt-premium-stat">
                <div className="srt-stat-icon">
                  <Eye size={17} />
                </div>

                <div>
                  <strong>{resumeAccess.remainingResumeViews}</strong>

                  <span>Resume Views Left</span>
                </div>
              </div>
            )}

            <div className="srt-header-stat">
              <div className="srt-stat-icon">
                <User size={17} />
              </div>

              <div>
                <strong>{filteredTrainers.length}</strong>

                <span>Trainers Available</span>
              </div>
            </div>
          </div>
        </div>

        {/* =================================================
            MAIN CONTENT
        ================================================= */}

        <div className="srt-marketplace-layout">
          {/* =================================================
              FILTER SIDEBAR
          ================================================= */}

          <aside className="srt-filter-sidebar">
            <div className="srt-filter-header">
              <div className="srt-filter-title">
                <SlidersHorizontal size={18} />

                <div>
                  <h3>Filters</h3>
                  <span>Refine trainers</span>
                </div>
              </div>

              {hasActiveFilters && (
                <button className="srt-clear-filter-btn" onClick={clearFilters}>
                  <RotateCcw size={13} />
                  Clear
                </button>
              )}
            </div>

            {/* SEARCH */}

            <div className="srt-filter-group">
              <label>Search</label>

              <div className="srt-filter-search">
                <Search size={15} />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name or keyword"
                />

                {search && (
                  <button onClick={() => setSearch("")}>
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            {/* LOCATION */}

            <div className="srt-filter-group">
              <label>Location</label>

              <div className="srt-select-wrapper">
                <MapPin size={15} />

                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All Locations</option>

                  {locations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>

                <ChevronDown size={14} />
              </div>
            </div>

            {/* SKILL */}

            <div className="srt-filter-group">
              <label>Primary Skill</label>

              <div className="srt-select-wrapper">
                <BriefcaseBusiness size={15} />

                <select
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                >
                  <option value="">All Skills</option>

                  {allSkills.map((skill) => (
                    <option key={skill} value={skill}>
                      {skill}
                    </option>
                  ))}
                </select>

                <ChevronDown size={14} />
              </div>
            </div>

            {/* EXPERIENCE */}

            <div className="srt-filter-group">
              <label>Experience</label>

              <div className="srt-filter-options">
                {[
                  ["", "Any Experience"],
                  ["0-2", "0 - 2 Years"],
                  ["3-5", "3 - 5 Years"],
                  ["6-10", "6 - 10 Years"],
                  ["10+", "10+ Years"],
                ].map(([value, label]) => (
                  <label className="srt-radio-option" key={value}>
                    <input
                      type="radio"
                      name="experience"
                      checked={experienceFilter === value}
                      onChange={() => setExperienceFilter(value)}
                    />

                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* RATE */}

            <div className="srt-filter-group">
              <label>Training Rate</label>

              <div className="srt-filter-options">
                {[
                  ["", "Any Rate"],
                  ["under-1000", "Below ₹1,000"],
                  ["1000-2500", "₹1,000 - ₹2,500"],
                  ["2500+", "Above ₹2,500"],
                ].map(([value, label]) => (
                  <label className="srt-radio-option" key={value}>
                    <input
                      type="radio"
                      name="rate"
                      checked={rateFilter === value}
                      onChange={() => setRateFilter(value)}
                    />

                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* STATUS */}

            <div className="srt-filter-group">
              <label>Trainer Status</label>

              <div className="srt-status-filter">
                <CheckCircle2 size={15} />

                <span>Approved Trainers</span>

                <div className="srt-status-dot" />
              </div>
            </div>

            {/* RESULTS */}

            <div className="srt-filter-result-box">
              <span>Matching Trainers</span>

              <strong>{filteredTrainers.length}</strong>
            </div>
          </aside>

          {/* =================================================
              RESULTS
          ================================================= */}

          <main className="srt-trainer-results">
            <div className="srt-results-toolbar">
              <div>
                <h2>Available Trainers</h2>

                <span>{filteredTrainers.length} approved trainers found</span>
              </div>

              {hasActiveFilters && (
                <button className="srt-mobile-clear" onClick={clearFilters}>
                  <RotateCcw size={14} />
                  Reset Filters
                </button>
              )}
            </div>

            {/* ERROR */}

            {error && (
              <div className="srt-error-box">
                <AlertCircle size={18} />

                <div>
                  <strong>Unable to load trainers</strong>

                  <span>{error}</span>
                </div>

                <button onClick={fetchTrainers}>Retry</button>
              </div>
            )}

            {/* LOADING */}

            {loading && (
              <div className="srt-state-container">
                <RefreshCw size={28} className="srt-spin" />

                <h3>Loading Trainers...</h3>

                <p>Fetching approved trainers</p>
              </div>
            )}

            {/* EMPTY */}

            {!loading && !error && filteredTrainers.length === 0 && (
              <div className="srt-state-container">
                <div className="srt-empty-icon">
                  <User size={28} />
                </div>

                <h3>No Trainers Found</h3>

                <p>Try changing your filters or search.</p>

                <button className="srt-reset-empty-btn" onClick={clearFilters}>
                  Reset Filters
                </button>
              </div>
            )}

            {/* =================================================
                TRAINER CARDS — full trainer + trainer_details data
            ================================================= */}

            {!loading && !error && filteredTrainers.length > 0 && (
              <div className="srt-trainer-card-list">
                {filteredTrainers.map((trainer, index) => {
                  const trainerId = getTrainerId(trainer);
                  const trainerName =
                    trainer?.name || trainer?.fullName || "Trainer";
                  const allSkills = getAllSkillsFull(trainer);
                  const location = getLocation(trainer);
                  const totalExp =
                    getTotalExperience(trainer) || getExperience(trainer);
                  const trainingExp = getTrainingExperience(trainer);
                  const industryExp = getIndustryExperience(trainer);
                  const rate = getRate(trainer);
                  const professionalTitle = getProfessionalTitle(trainer);
                  const orgLine = [
                    getCurrentDesignation(trainer),
                    getCurrentOrganization(trainer),
                  ]
                    .filter(Boolean)
                    .join(" @ ");
                  const photoUrl = getProfilePictureUrl(trainer);
                  const summary = getProfileSummary(trainer);
                  const certifications = getCertifications(trainer);
                  const languagesKnown = getLanguagesKnown(trainer);
                  const preferredLocations = getPreferredLocations(trainer);
                  const teachingMode = getTeachingMode(trainer);

                  return (
                    <div
                      className="srt-trainer-card"
                      key={trainerId || `${trainerName}-${index}`}
                    >
                      {/* TOP ROW: identity + title/org + rate + resume + actions */}
                      <div className="srt-tc-top">
                        <div className="srt-tc-identity">
                          {photoUrl ? (
                            <img
                              className="srt-tc-avatar srt-tc-avatar-photo"
                              src={photoUrl}
                              alt={trainerName}
                            />
                          ) : (
                            <div className="srt-tc-avatar">
                              {getInitial(trainerName)}
                            </div>
                          )}

                          <div className="srt-tc-name-block">
                            <span className="srt-tc-name" title={trainerName}>
                              {trainerName}
                            </span>
                            <span className="srt-approved-label-sm">
                              <CheckCircle2 size={10} />
                              Approved
                            </span>
                          </div>
                        </div>

                        <div className="srt-tc-title-block">
                          <div className="srt-row-title" title={professionalTitle}>
                            {professionalTitle || "—"}
                          </div>
                          {orgLine && (
                            <div className="srt-row-org" title={orgLine}>
                              <Building2 size={10} />
                              {orgLine}
                            </div>
                          )}
                        </div>

                        <div className="srt-tc-rate-block">
                          {rate !== "" && rate !== null && rate !== undefined ? (
                            <span className="srt-row-inline srt-rate-value">
                              <IndianRupee size={13} />
                              {Number(rate).toLocaleString("en-IN")}
                              <em>/hr</em>
                            </span>
                          ) : (
                            <span className="srt-row-muted">Rate N/A</span>
                          )}
                        </div>

                        <div
                          className={`srt-resume-status-sm ${hasPurchasedResumeAccess ? "srt-active" : ""
                            }`}
                        >
                          {hasPurchasedResumeAccess ? (
                            <Eye size={10} />
                          ) : (
                            <Lock size={10} />
                          )}
                          <span>
                            {hasPurchasedResumeAccess
                              ? `${resumeAccess.remainingResumeViews} left`
                              : "Locked"}
                          </span>
                        </div>

                        <div className="srt-row-actions">
                          <button
                            className="srt-icon-btn srt-profile-icon-btn"
                            title="View Profile"
                            onClick={() => openProfileModal(trainer)}
                          >
                            <UserRound size={14} />
                          </button>

                          <button
                            className="srt-icon-btn srt-resume-icon-btn"
                            title="View Resume"
                            onClick={() => handleViewResume(trainer)}
                            disabled={resumeLoadingId === trainerId}
                          >
                            {resumeLoadingId === trainerId ? (
                              <RefreshCw size={14} className="srt-spin" />
                            ) : (
                              <Eye size={14} />
                            )}
                          </button>

                          <button
                            className="srt-icon-btn srt-request-icon-btn"
                            title="Send Request"
                            onClick={() => handleSendRequest(trainer)}
                            disabled={requestingTrainerId === trainerId}
                          >
                            {requestingTrainerId === trainerId ? (
                              <RefreshCw size={14} className="srt-spin" />
                            ) : (
                              <Send size={14} />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* META ROW: location + all experience buckets + teaching mode */}
                      <div className="srt-tc-meta-row">
                        <span className="srt-row-inline" title={location}>
                          <MapPin size={12} />
                          {location}
                        </span>

                        <span className="srt-row-inline">
                          <Clock3 size={12} />
                          Total: {totalExp || "N/A"}
                        </span>

                        {trainingExp && (
                          <span className="srt-row-inline">
                            <GraduationCap size={12} />
                            Training: {trainingExp}
                          </span>
                        )}

                        {industryExp && (
                          <span className="srt-row-inline">
                            <Building2 size={12} />
                            Industry: {industryExp}
                          </span>
                        )}

                        {teachingMode && (
                          <span className="srt-row-inline">
                            <Globe size={12} />
                            {teachingMode}
                          </span>
                        )}
                      </div>

                      {/* SUMMARY */}
                      {summary && (
                        <div className="srt-tc-summary" title={summary}>
                          {summary}
                        </div>
                      )}

                      {/* SKILLS: full trainer_details skill set, no truncation */}
                      {allSkills.length > 0 && (
                        <div className="srt-row-skills-full">
                          {allSkills.map((skill, i) => (
                            <span key={i}>{skill}</span>
                          ))}
                        </div>
                      )}

                      {/* EXTRA: certifications + languages known + preferred locations */}
                      {(certifications.length > 0 ||
                        languagesKnown.length > 0 ||
                        preferredLocations.length > 0) && (
                          <div className="srt-tc-extra-row">
                            {certifications.length > 0 && (
                              <span className="srt-tc-extra-item">
                                <Award size={11} />
                                {certifications.join(", ")}
                              </span>
                            )}

                            {languagesKnown.length > 0 && (
                              <span className="srt-tc-extra-item">
                                <Languages size={11} />
                                {languagesKnown.join(", ")}
                              </span>
                            )}

                            {preferredLocations.length > 0 && (
                              <span className="srt-tc-extra-item">
                                <MapPin size={11} />
                                {preferredLocations.join(", ")}
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* =====================================================
          RESUME PREVIEW MODAL
      ===================================================== */}

      {resumePreviewOpen && (
        <div className="srt-modal-overlay" onClick={closeResumePreview}>
          <div className="srt-resume-modal" onClick={(e) => e.stopPropagation()}>
            <div className="srt-modal-header">
              <div>
                <span>TRAINER RESUME</span>

                <h2>{resumePreviewName}</h2>
              </div>

              <button onClick={closeResumePreview}>
                <X size={19} />
              </button>
            </div>

            <div className="srt-resume-frame">
              <iframe src={resumePreviewUrl} title="Trainer Resume" />
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          FULL PROFILE MODAL (fetched via /trainers/{id}/profile)
      ===================================================== */}

      {showProfileModal && (
        <div className="srt-modal-overlay" onClick={closeProfileModal}>
          <div className="srt-profile-modal" onClick={(e) => e.stopPropagation()}>
            {/* LOADING STATE */}
            {profileLoading && (
              <>
                <div className="srt-modal-header srt-profile-modal-header">
                  <div>
                    <span>TRAINER PROFILE</span>
                    <h2>Loading...</h2>
                  </div>

                  <button onClick={closeProfileModal}>
                    <X size={19} />
                  </button>
                </div>

                <div className="srt-plans-state">
                  <RefreshCw size={28} className="srt-spin" />
                  <h3>Loading Profile...</h3>
                  <p>Fetching trainer details</p>
                </div>
              </>
            )}

            {/* ERROR STATE */}
            {!profileLoading && profileError && (
              <>
                <div className="srt-modal-header srt-profile-modal-header">
                  <div>
                    <span>TRAINER PROFILE</span>
                    <h2>Error</h2>
                  </div>

                  <button onClick={closeProfileModal}>
                    <X size={19} />
                  </button>
                </div>

                <div className="srt-plans-state">
                  <AlertCircle size={28} />
                  <h3>Unable to Load Profile</h3>
                  <p>{profileError}</p>

                  <button onClick={() => openProfileModal(profileTarget || {})}>
                    Retry
                  </button>
                </div>
              </>
            )}

            {/* PROFILE CONTENT */}
            {!profileLoading &&
              !profileError &&
              profileModalTrainer &&
              (() => {
                const trainer = profileModalTrainer;

                const trainerName =
                  trainer?.name || trainer?.fullName || "Trainer";

                const photoUrl = getProfilePictureUrl(trainer);
                const professionalTitle = getProfessionalTitle(trainer);
                const currentOrg = getCurrentOrganization(trainer);
                const currentDesignation = getCurrentDesignation(trainer);

                const orgLine = [currentDesignation, currentOrg]
                  .filter(Boolean)
                  .join(" @ ");

                const summary = getProfileSummary(trainer);
                const totalExp = getTotalExperience(trainer);
                const trainingExp = getTrainingExperience(trainer);
                const industryExp = getIndustryExperience(trainer);
                const technicalSkills = getTechnicalSkills(trainer);
                const programmingLanguages = getProgrammingLanguages(trainer);
                const frameworks = getFrameworks(trainer);
                const databaseTech = getDatabaseTechnologies(trainer);
                const cloudTools = getCloudTools(trainer);
                const devopsTools = getDevopsTools(trainer);
                const certifications = getCertifications(trainer);
                const achievements = getAchievements(trainer);
                const notableProjects = getNotableProjects(trainer);
                const languagesKnown = getLanguagesKnown(trainer);
                const preferredLocations = getPreferredLocations(trainer);
                const teachingMode = getTeachingMode(trainer);
                const linkedinUrl = getLinkedinUrl(trainer);
                const githubUrl = getGithubUrl(trainer);
                const portfolioUrl = getPortfolioUrl(trainer);
                const createdAt = getCreatedAt(trainer);
                const updatedAt = getUpdatedAt(trainer);
                const trainerId = getTrainerId(trainer);

                const skillSections = [
                  ["Technical Skills", technicalSkills],
                  ["Programming Languages", programmingLanguages],
                  ["Frameworks", frameworks],
                  ["Database Technologies", databaseTech],
                  ["Cloud Tools", cloudTools],
                  ["DevOps Tools", devopsTools],
                ].filter(([, list]) => list.length > 0);

                return (
                  <>
                    <div className="srt-modal-header srt-profile-modal-header">
                      <div>
                        <span>TRAINER PROFILE</span>
                        <h2>{trainerName}</h2>
                      </div>

                      <button onClick={closeProfileModal}>
                        <X size={19} />
                      </button>
                    </div>

                    <div className="srt-profile-modal-body">
                      {/* HERO */}
                      <div className="srt-profile-hero">
                        {photoUrl ? (
                          <img
                            className="srt-profile-avatar-lg"
                            src={photoUrl}
                            alt={trainerName}
                          />
                        ) : (
                          <div className="srt-profile-avatar-lg srt-profile-avatar-fallback">
                            {getInitial(trainerName)}
                          </div>
                        )}

                        <div className="srt-profile-hero-info">
                          {professionalTitle && (
                            <div className="srt-profile-title">
                              {professionalTitle}
                            </div>
                          )}

                          {orgLine && (
                            <div className="srt-profile-org">
                              <Building2 size={13} />
                              {orgLine}
                            </div>
                          )}

                          <div className="srt-profile-badges">
                            <span className="srt-approved-label-sm">
                              <CheckCircle2 size={11} />
                              Approved
                            </span>

                            {teachingMode && (
                              <span className="srt-teaching-badge">
                                <GraduationCap size={11} />
                                {teachingMode}
                              </span>
                            )}
                          </div>

                          {(linkedinUrl || githubUrl || portfolioUrl) && (
                            <div className="srt-social-links">
                              {linkedinUrl && (
                                <a
                                  href={linkedinUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="srt-social-link"
                                >
                                  LinkedIn
                                </a>
                              )}

                              {githubUrl && (
                                <a
                                  href={githubUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="srt-social-link"
                                >
                                  GitHub
                                </a>
                              )}

                              {portfolioUrl && (
                                <a
                                  href={portfolioUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="srt-social-link"
                                >
                                  <Globe size={13} />
                                  Portfolio
                                </a>
                              )}
                            </div>
                          )}

                          {(updatedAt || createdAt) && (
                            <div className="srt-profile-meta-line">
                              <History size={11} />
                              {updatedAt
                                ? `Profile updated on ${updatedAt}`
                                : `Profile created on ${createdAt}`}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* EXPERIENCE STATS */}
                      {(totalExp || trainingExp || industryExp) && (
                        <div className="srt-stats-row">
                          <div className="srt-stat-box">
                            <strong>{totalExp || "-"}</strong>
                            <span>Total Experience</span>
                          </div>

                          <div className="srt-stat-box">
                            <strong>{trainingExp || "-"}</strong>
                            <span>Training Experience</span>
                          </div>

                          <div className="srt-stat-box">
                            <strong>{industryExp || "-"}</strong>
                            <span>Industry Experience</span>
                          </div>
                        </div>
                      )}

                      {/* SUMMARY */}
                      {summary && (
                        <div className="srt-section-block">
                          <div className="srt-section-label">Profile Summary</div>
                          <p className="srt-section-text">{summary}</p>
                        </div>
                      )}

                      {/* SKILLS */}
                      {skillSections.map(([label, list]) => (
                        <div className="srt-section-block" key={label}>
                          <div className="srt-section-label">{label}</div>
                          <div className="srt-chip-group">
                            {list.map((item, i) => (
                              <span key={i}>{item}</span>
                            ))}
                          </div>
                        </div>
                      ))}

                      {/* CERTIFICATIONS */}
                      {certifications.length > 0 && (
                        <div className="srt-section-block">
                          <div className="srt-section-label">
                            <Award size={13} />
                            Certifications
                          </div>
                          <div className="srt-list-block">
                            {certifications.map((item, i) => (
                              <div className="srt-list-item" key={i}>
                                <CheckCircle2 size={12} />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ACHIEVEMENTS */}
                      {achievements.length > 0 && (
                        <div className="srt-section-block">
                          <div className="srt-section-label">
                            <Trophy size={13} />
                            Achievements
                          </div>
                          <div className="srt-list-block">
                            {achievements.map((item, i) => (
                              <div className="srt-list-item" key={i}>
                                <CheckCircle2 size={12} />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* PROJECTS */}
                      {notableProjects.length > 0 && (
                        <div className="srt-section-block">
                          <div className="srt-section-label">
                            <FolderGit2 size={13} />
                            Notable Projects
                          </div>
                          <div className="srt-list-block">
                            {notableProjects.map((item, i) => (
                              <div className="srt-list-item" key={i}>
                                <CheckCircle2 size={12} />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* LANGUAGES + LOCATIONS */}
                      <div className="srt-section-grid">
                        {languagesKnown.length > 0 && (
                          <div className="srt-section-block">
                            <div className="srt-section-label">
                              <Languages size={13} />
                              Languages Known
                            </div>
                            <div className="srt-chip-group">
                              {languagesKnown.map((item, i) => (
                                <span key={i}>{item}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {preferredLocations.length > 0 && (
                          <div className="srt-section-block">
                            <div className="srt-section-label">
                              <MapPin size={13} />
                              Preferred Locations
                            </div>
                            <div className="srt-chip-group">
                              {preferredLocations.map((item, i) => (
                                <span key={i}>{item}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="srt-profile-modal-footer">
                      <button
                        className="srt-profile-download-btn"
                        onClick={() => handleDownloadProfile(trainer)}
                        disabled={profileDownloadLoadingId === trainerId}
                      >
                        {profileDownloadLoadingId === trainerId ? (
                          <RefreshCw size={14} className="srt-spin" />
                        ) : (
                          <FileText size={14} />
                        )}

                        {profileDownloadLoadingId === trainerId
                          ? "Preparing..."
                          : "Download Profile"}
                      </button>
                      <button
                        className="srt-resume-btn"
                        onClick={() => handleViewResume(trainer)}
                        disabled={resumeLoadingId === trainerId}
                      >
                        {resumeLoadingId === trainerId ? (
                          <RefreshCw size={14} className="srt-spin" />
                        ) : (
                          <Eye size={14} />
                        )}
                        View Resume
                      </button>

                    
{getCurrentUser()?.role?.toUpperCase() !== "ADMIN" && (
  <button
    className="srt-request-btn"
    onClick={() => handleSendRequest(trainer)}
    disabled={requestingTrainerId === trainerId}
  >
    {requestingTrainerId === trainerId ? (
      <RefreshCw size={14} className="srt-spin" />
    ) : (
      <Send size={14} />
    )}
    Send Request
  </button>
)}

                    </div>
                  </>
                );
              })()}
          </div>
        </div>
      )}

      {/* =====================================================
          LIMIT MODAL
      ===================================================== */}

      {showSubscriptionModal && (
        <div
          className="srt-modal-overlay"
          onClick={() => setShowSubscriptionModal(false)}
        >
          <div className="srt-limit-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="srt-floating-close"
              onClick={() => setShowSubscriptionModal(false)}
            >
              <X size={18} />
            </button>

            <div className="srt-limit-icon">
              <Lock size={25} />
            </div>

            <div className="srt-premium-label">
              <Sparkles size={12} />
              PREMIUM ACCESS
            </div>

            <h2>Resume Access Limit Reached</h2>

            <p>
              Your free trainer resume views have been used. Purchase a Resume
              Access plan to continue.
            </p>

            <div className="srt-limit-info">
              <div>
                <strong>2</strong>
                <span>FREE VIEWS</span>
              </div>

              <div className="srt-limit-divider" />

              <div>
                <strong>Multiple</strong>
                <span>PLAN OPTIONS</span>
              </div>
            </div>

            <button className="srt-plans-btn" onClick={openPlans}>
              <CreditCard size={16} />
              View Resume Access Plans
              <ChevronRight size={16} />
            </button>

            <button
              className="srt-later-btn"
              onClick={() => setShowSubscriptionModal(false)}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* =====================================================
          PLANS MODAL
      ===================================================== */}

      {showPlansModal && (
        <div className="srt-modal-overlay" onClick={() => setShowPlansModal(false)}>
          <div className="srt-plans-modal" onClick={(e) => e.stopPropagation()}>
            <div className="srt-plans-header">
              <div>
                <div className="srt-plans-eyebrow">
                  <ShieldCheck size={14} />
                  CERTED PREMIUM
                </div>

                <h2>Trainer Resume Access</h2>

                <p>Choose a plan to access professional trainer resumes.</p>
              </div>

              <button onClick={() => setShowPlansModal(false)}>
                <X size={19} />
              </button>
            </div>

            <div className="srt-usage-box">
              <div>
                <Eye size={17} />

                <div>
                  <strong>
                    {resumeAccess.hasPurchasedPlan
                      ? resumeAccess.planName
                      : "Free Resume Access"}
                  </strong>

                  <span>
                    {resumeAccess.hasPurchasedPlan
                      ? "Purchased access"
                      : "Free resume views included"}
                  </span>
                </div>
              </div>

              <strong>
                {resumeAccess.hasPurchasedPlan
                  ? resumeAccess.remainingResumeViews
                  : 2}
              </strong>
            </div>

            {plansLoading && (
              <div className="srt-plans-state">
                <RefreshCw size={28} className="srt-spin" />

                <h3>Loading Plans...</h3>
              </div>
            )}

            {!plansLoading && plansError && (
              <div className="srt-plans-state">
                <AlertCircle size={28} />

                <h3>Unable to Load Plans</h3>

                <p>{plansError}</p>

                <button onClick={fetchSubscriptionProducts}>Retry</button>
              </div>
            )}

            {!plansLoading && !plansError && subscriptionProducts.length === 0 && (
              <div className="srt-plans-state">
                <FileText size={30} />

                <h3>No Resume Plans</h3>

                <p>No active resume access plans are currently available.</p>
              </div>
            )}

            {!plansLoading && !plansError && subscriptionProducts.length > 0 && (
              <div className="srt-plans-grid">
                {subscriptionProducts.map((product, index) => {
                  const productId =
                    product?.id ??
                    product?.productId ??
                    product?.subscriptionProductId ??
                    index;

                  const price =
                    product?.price ??
                    product?.amount ??
                    product?.sellingPrice ??
                    null;

                  const limit = getResumeLimit(product);
                  const features = getProductFeatures(product);

                  const popular =
                    product?.isPopular === true ||
                    product?.popular === true ||
                    product?.recommended === true;

                  return (
                    <div
                      className={`srt-plan-card ${popular ? "srt-popular" : ""}`}
                      key={productId}
                    >
                      {popular && (
                        <div className="srt-popular-badge">
                          <Star size={11} fill="currentColor" />
                          Recommended
                        </div>
                      )}

                      <div className="srt-plan-icon">
                        <FileText size={20} />
                      </div>

                      <h3>{getProductName(product)}</h3>

                      <p>{getProductDescription(product)}</p>

                      <div className="srt-plan-price">
                        <span>₹</span>

                        <strong>{formatPrice(price)}</strong>
                      </div>

                      {getProductValidity(product) && (
                        <div className="srt-plan-validity">
                          <CalendarDays size={14} />

                          {getProductValidity(product)}
                        </div>
                      )}

                      <div className="srt-plan-resumes">
                        <Eye size={16} />

                        <div>
                          <span>Resume Access</span>

                          <strong>
                            {limit ?? "As per plan"} Resume
                            {Number(limit) === 1 ? "" : "s"}
                          </strong>
                        </div>
                      </div>

                      {features.length > 0 && (
                        <div className="srt-features">
                          {features.map((feature, i) => (
                            <div key={i}>
                              <CheckCircle2 size={14} />

                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        className="srt-purchase-btn"
                        onClick={() => handlePurchasePlan(product)}
                        disabled={purchaseLoadingId === productId}
                      >
                        {purchaseLoadingId === productId ? (
                          <>
                            <RefreshCw size={14} className="srt-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard size={14} />
                            Purchase Plan
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {inviteModalOpen && (
        <div className="invite-overlay">
          <div
            className="invite-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invite-modal-title"
          >
            <div className="invite-header">
              <div>
                <h2 id="invite-modal-title">Invite Trainer</h2>
                <p>
                  Send a job opportunity to{" "}
                  {inviteTrainer?.name ||
                    inviteTrainer?.fullName ||
                    inviteTrainer?.full_name ||
                    "this trainer"}
                </p>
              </div>

              <button
                type="button"
                className="invite-close"
                onClick={() => {
                  setInviteModalOpen(false);
                  setInviteError("");
                }}
                aria-label="Close invitation form"
              >
                ×
              </button>
            </div>

            <form onSubmit={submitTrainerInvite}>
              <label htmlFor="invite-job">Select an approved job</label>

              {inviteJobsLoading ? (
                <p>Loading your jobs...</p>
              ) : (
                <select
                  id="invite-job"
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  required
                  disabled={partnerJobs.length === 0}
                >
                  <option value="">Choose a job</option>

                  {partnerJobs.map((job) => (
                    <option key={job.jobId} value={job.jobId}>
                      {job.jobTitle} — {job.location || "Location not specified"}
                    </option>
                  ))}
                </select>
              )}

              {(() => {
                const job = partnerJobs.find(
                  (item) => String(item.jobId) === String(selectedJobId)
                );

                if (!job) return null;

                return (
                  <div className="invite-job-details">
                    <h3>{job.jobTitle}</h3>

                    <p>
                      <strong>Location:</strong>{" "}
                      {job.location || "Not specified"}
                    </p>

                    <p>
                      <strong>Employment:</strong>{" "}
                      {job.employeeType || "Not specified"}
                    </p>

                    <p>
                      <strong>Experience:</strong>{" "}
                      {job.experienceRequired || "Not specified"}
                    </p>

                    <p>
                      <strong>Skills:</strong>{" "}
                      {job.skillsRequired || "Not specified"}
                    </p>

                    <p>
                      <strong>Vacancies:</strong>{" "}
                      {job.vacancy ?? "Not specified"}
                    </p>

                    <p>
                      <strong>Description:</strong>{" "}
                      {job.jobDescription || "No description provided."}
                    </p>
                  </div>
                );
              })()}

              <label htmlFor="invite-message">
                Personal message <span>(optional)</span>
              </label>

              <textarea
                id="invite-message"
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                placeholder="Write a short message for the trainer..."
                rows={4}
                maxLength={1000}
              />

              {inviteError && (
                <p className="invite-error" role="alert">
                  {inviteError}
                </p>
              )}

              <div className="invite-actions">
                <button
                  type="button"
                  className="invite-cancel"
                  onClick={() => {
                    setInviteModalOpen(false);
                    setInviteError("");
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="invite-submit"
                  disabled={
                    inviteJobsLoading ||
                    !selectedJobId ||
                    requestingTrainerId !== null
                  }
                >
                  {requestingTrainerId === getTrainerId(inviteTrainer)
                    ? "Sending..."
                    : "Send Invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* =====================================================
          CSS
      ===================================================== */}

      <style>{`

/* =========================================================
   CERTED TRAINER MARKETPLACE
========================================================= */
.srt-profile-modal-footer .srt-profile-download-btn {
  color: #3E0F8D;
  background: #F7F3FC;
  border: 1px solid #E1D5F1;
}
  
.invite-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(23, 18, 31, 0.58);
}

.invite-modal {
  width: 100%;
  max-width: 620px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 26px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 70px rgba(38, 6, 92, 0.25);
}

.invite-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.invite-header h2 {
  margin: 0 0 6px;
  color: #3e0f8d;
  font-size: 23px;
}

.invite-header p {
  margin: 0;
  color: #716984;
  font-size: 14px;
}

.invite-close {
  border: 0;
  background: transparent;
  color: #625a6b;
  font-size: 30px;
  cursor: pointer;
}

.invite-modal form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.invite-modal label {
  margin-top: 8px;
  color: #2b0c61;
  font-size: 14px;
  font-weight: 600;
}

.invite-modal label span {
  color: #8b8492;
  font-weight: 400;
}

.invite-modal select,
.invite-modal textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid #e7def3;
  border-radius: 10px;
  background: #fff;
  color: #17121f;
  font: inherit;
  outline: none;
}

.invite-modal select:focus,
.invite-modal textarea:focus {
  border-color: #9564dd;
  box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.13);
}

.invite-modal textarea {
  resize: vertical;
}

.invite-job-details {
  margin: 8px 0;
  padding: 16px;
  border: 1px solid #e7def3;
  border-radius: 12px;
  background: #f8f5fc;
}

.invite-job-details h3 {
  margin: 0 0 12px;
  color: #3e0f8d;
  font-size: 17px;
}

.invite-job-details p {
  margin: 7px 0;
  color: #625a6b;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.invite-error {
  margin: 6px 0;
  color: #b42318;
  font-size: 13px;
}

.invite-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.invite-cancel,
.invite-submit {
  padding: 11px 18px;
  border-radius: 9px;
  font-weight: 600;
  cursor: pointer;
}

.invite-cancel {
  border: 1px solid #e7def3;
  background: #fff;
  color: #3e0f8d;
}

.invite-submit {
  border: 0;
  background: #3e0f8d;
  color: #fff;
}

.invite-submit:hover {
  background: #26065c;
}

.invite-submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

@media (max-width: 520px) {
  .invite-modal {
    padding: 18px;
  }

  .invite-actions {
    flex-direction: column-reverse;
  }

  .invite-actions button {
    width: 100%;
  }
}


.srt-profile-modal-footer .srt-profile-download-btn:hover:not(:disabled) {
  color: #FFFFFF;
  background: #3E0F8D;
  border-color: #3E0F8D;
}

.srt-profile-modal-footer button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.srt-certed-trainer-page {
  --primary: #3E0F8D;
  --secondary: #9564DD;
  --accent: #E4DA72;
  --text: #302A3A;
  --muted: #77717F;
  --border: #E4E0EA;
  --light-bg: #F8F7FB;
  --white: #FFFFFF;
  --success: #16875B;
  --success-bg: #EAF8F1;
  --danger: #C83B4A;
  --danger-bg: #FFF1F2;

  width: 100%;
  min-height: 100vh;
  padding: 22px;
  background: #F7F6FA;
  color: var(--text);
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  box-sizing: border-box;
}

.srt-certed-trainer-page *,
.srt-certed-trainer-page *::before,
.srt-certed-trainer-page *::after {
  box-sizing: border-box;
}

/* Modals are rendered outside .srt-certed-trainer-page, so they need
   the same design tokens + base styles. */
.srt-modal-overlay {
  --primary: #3E0F8D;
  --secondary: #9564DD;
  --accent: #E4DA72;
  --text: #302A3A;
  --muted: #77717F;
  --border: #E4E0EA;
  --light-bg: #F8F7FB;
  --white: #FFFFFF;
  --success: #16875B;
  --success-bg: #EAF8F1;
  --danger: #C83B4A;
  --danger-bg: #FFF1F2;

  color: var(--text);
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
}

.srt-modal-overlay *,
.srt-modal-overlay *::before,
.srt-modal-overlay *::after {
  box-sizing: border-box;
}

/* =========================================================
   PAGE HEADER
========================================================= */

.srt-certed-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  padding: 18px 22px;
  margin-bottom: 18px;

  background: #FFFFFF;
  border: 1px solid var(--border);
  border-radius: 12px;

  box-shadow: 0 2px 10px rgba(48, 42, 58, 0.05);
}

.srt-certed-eyebrow {
  display: flex;
  align-items: center;
  gap: 6px;

  margin-bottom: 5px;

  color: var(--secondary);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.srt-certed-header h1 {
  margin: 0 0 4px;

  color: var(--text);
  font-size: 21px;
  font-weight: 750;
  line-height: 1.2;
}

.srt-certed-header p {
  margin: 0;

  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
}

/* =========================================================
   HEADER STATS
========================================================= */

.srt-header-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.srt-header-stat {
  display: flex;
  align-items: center;
  gap: 8px;

  min-width: 130px;
  padding: 7px 10px;

  background: #FAF9FC;
  border: 1px solid var(--border);
  border-radius: 9px;
}

.srt-stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 28px;
  height: 28px;

  color: var(--primary);
  background: #F0EAFB;
  border-radius: 8px;
}

.srt-header-stat strong {
  display: block;

  color: var(--primary);
  font-size: 14px;
  font-weight: 750;
  line-height: 1.1;
}

.srt-header-stat span {
  display: block;

  margin-top: 2px;

  color: var(--muted);
  font-size: 9px;
  font-weight: 600;
}

.srt-premium-stat {
  border-color: rgba(149, 100, 221, 0.28);
  background: linear-gradient(135deg, #FBF9FF, #F7F1FF);
}

/* =========================================================
   MAIN MARKETPLACE LAYOUT
========================================================= */

.srt-marketplace-layout {
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  align-items: start;
  gap: 14px;
}

/* =========================================================
   FILTER SIDEBAR
========================================================= */

.srt-filter-sidebar {
  position: sticky;
  top: 18px;

  padding: 16px;

  background: #FFFFFF;
  border: 1px solid var(--border);
  border-radius: 12px;

  box-shadow: 0 2px 10px rgba(48, 42, 58, 0.04);
}

.srt-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-bottom: 10px;
  margin-bottom: 11px;

  border-bottom: 1px solid #EEEAF2;
}

.srt-filter-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.srt-filter-title > svg {
  color: var(--secondary);
}

.srt-filter-title h3 {
  margin: 0;

  color: var(--text);
  font-size: 13px;
  font-weight: 750;
}

.srt-filter-title span {
  display: block;

  margin-top: 1px;

  color: #96909D;
  font-size: 9px;
}

/* CLEAR FILTER BUTTON */

.srt-clear-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 4px 6px;

  color: var(--secondary);
  background: #F7F2FD;

  border: 1px solid #E9DDF7;
  border-radius: 6px;

  font-size: 9px;
  font-weight: 700;

  cursor: pointer;
  transition: 0.2s ease;
}

.srt-clear-filter-btn:hover {
  color: #FFFFFF;
  background: var(--secondary);
  border-color: var(--secondary);
}

/* FILTER GROUP */

.srt-filter-group {
  margin-bottom: 12px;
}

.srt-filter-group > label {
  display: block;

  margin-bottom: 5px;

  color: #514B59;
  font-size: 10px;
  font-weight: 700;
}

/* SEARCH */

.srt-filter-search {
  display: flex;
  align-items: center;
  gap: 6px;

  width: 100%;
  min-height: 32px;
  padding: 0 8px;

  background: #FFFFFF;
  border: 1px solid #DDD9E4;
  border-radius: 7px;

  transition: all 0.2s ease;
}

.srt-filter-search:hover {
  border-color: #C7B4E5;
}

.srt-filter-search:focus-within {
  border-color: var(--secondary);
  box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.08);
}

.srt-filter-search > svg {
  flex-shrink: 0;
  color: #8D8795;
}

.srt-filter-search input {
  flex: 1;

  width: 100%;
  height: 30px;
  padding: 0;

  color: var(--text);
  background: transparent;

  border: none;
  outline: none;

  font-family: inherit;
  font-size: 11px;
}

.srt-filter-search input::placeholder {
  color: #A29CA9;
  font-size: 10px;
}

.srt-filter-search button {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 20px;
  height: 20px;
  padding: 0;

  color: #817B89;
  background: transparent;

  border: none;
  border-radius: 50%;

  cursor: pointer;
}

.srt-filter-search button:hover {
  color: var(--danger);
  background: #FFF0F1;
}

/* SELECT */

.srt-select-wrapper {
  position: relative;

  display: flex;
  align-items: center;
  gap: 6px;

  width: 100%;
  min-height: 32px;
  padding: 0 8px;

  background: #FFFFFF;
  border: 1px solid #DDD9E4;
  border-radius: 7px;

  transition: 0.2s ease;
}

.srt-select-wrapper:hover {
  border-color: #C7B4E5;
}

.srt-select-wrapper:focus-within {
  border-color: var(--secondary);
  box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.08);
}

.srt-select-wrapper > svg:first-child {
  flex-shrink: 0;
  color: #8D8795;
}

.srt-select-wrapper select {
  flex: 1;

  width: 100%;
  min-width: 0;
  height: 30px;

  padding: 0 22px 0 0;

  color: var(--text);
  background: transparent;

  border: none;
  outline: none;

  appearance: none;
  -webkit-appearance: none;

  font-family: inherit;
  font-size: 10px;
  font-weight: 500;

  cursor: pointer;
}

.srt-select-wrapper > svg:last-child {
  position: absolute;
  right: 8px;

  pointer-events: none;
  color: #88818F;
}

/* FILTER OPTIONS */

.srt-filter-options {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.srt-radio-option {
  display: flex !important;
  align-items: center;
  gap: 7px;

  margin: 0 !important;

  color: #696270;
  font-size: 10px !important;
  font-weight: 500 !important;

  cursor: pointer;
}

.srt-radio-option input {
  width: 13px;
  height: 13px;

  margin: 0;

  accent-color: var(--secondary);

  cursor: pointer;
}

.srt-radio-option span {
  transition: color 0.2s ease;
}

.srt-radio-option:hover span {
  color: var(--primary);
}

/* STATUS FILTER */

.srt-status-filter {
  display: flex;
  align-items: center;
  gap: 6px;

  min-height: 32px;
  padding: 0 8px;

  color: var(--success);
  background: var(--success-bg);

  border: 1px solid #CBEBDD;
  border-radius: 7px;

  font-size: 9px;
  font-weight: 650;
}

.srt-status-filter svg {
  flex-shrink: 0;
}

.srt-status-dot {
  width: 6px;
  height: 6px;
  margin-left: auto;

  background: #20A66D;
  border-radius: 50%;

  box-shadow: 0 0 0 3px rgba(32, 166, 109, 0.12);
}

/* FILTER RESULT */

.srt-filter-result-box {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: 3px;
  padding: 9px;

  background: linear-gradient(135deg, #F8F4FF, #FBFAFD);

  border: 1px solid #E7DDF5;
  border-radius: 8px;
}

.srt-filter-result-box span {
  color: #756E80;
  font-size: 9px;
  font-weight: 600;
}

.srt-filter-result-box strong {
  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 24px;
  height: 21px;
  padding: 0 6px;

  color: #FFFFFF;
  background: var(--primary);

  border-radius: 6px;

  font-size: 10px;
}

/* =========================================================
   RESULTS AREA
========================================================= */

.srt-trainer-results {
  min-width: 0;
}

.srt-results-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 11px;
}

.srt-results-toolbar h2 {
  margin: 0 0 2px;

  color: var(--text);
  font-size: 15px;
  font-weight: 750;
}

.srt-results-toolbar span {
  color: #88818E;
  font-size: 10px;
}

.srt-mobile-clear {
  display: none;
}

/* =========================================================
   TRAINER CARDS — roomy cards showing trainer + trainer_details
========================================================= */

.srt-trainer-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.srt-trainer-card {
  display: flex;
  flex-direction: column;
  gap: 8px;

  padding: 14px 16px;

  background: #FFFFFF;
  border: 1px solid var(--border);
  border-radius: 12px;

  box-shadow: 0 2px 10px rgba(48, 42, 58, 0.045);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.srt-trainer-card:hover {
  border-color: #CBB6E5;
  box-shadow: 0 8px 20px rgba(62, 15, 141, 0.1);
  transform: translateY(-1px);
}

/* TOP ROW */

.srt-tc-top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.srt-tc-identity {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1.1 1 190px;
}

.srt-tc-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 38px;
  height: 38px;

  color: #FFFFFF;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border-radius: 9px;

  font-size: 15px;
  font-weight: 750;
}

.srt-tc-avatar-photo {
  object-fit: cover;
}

.srt-tc-name-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.srt-tc-name {
  overflow: hidden;

  color: var(--text);
  font-size: 14px;
  font-weight: 700;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.srt-tc-title-block {
  min-width: 0;
  overflow: hidden;
  flex: 1.3 1 200px;
}

.srt-approved-label-sm {
  display: inline-flex;
  align-items: center;
  gap: 3px;

  width: fit-content;

  color: var(--success);
  font-size: 9px;
  font-weight: 700;
}

.srt-row-title {
  overflow: hidden;

  color: var(--secondary);
  font-size: 12px;
  font-weight: 650;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.srt-row-org {
  display: flex;
  align-items: center;
  gap: 4px;

  overflow: hidden;
  margin-top: 2px;

  color: #8A8390;
  font-size: 10px;
  font-weight: 500;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.srt-row-org svg {
  flex-shrink: 0;
  color: #B0A8BB;
}

.srt-tc-rate-block {
  flex: 0 0 auto;
  white-space: nowrap;
}

.srt-rate-value {
  color: var(--primary) !important;
  font-size: 13px !important;
  font-weight: 750 !important;
}

.srt-rate-value em {
  margin-left: 2px;

  color: #A79FAE;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
}

.srt-row-inline {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  max-width: 100%;
  overflow: hidden;

  color: #6E6775;
  font-size: 11px;
  font-weight: 550;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.srt-row-inline svg {
  flex-shrink: 0;
  color: var(--secondary);
}

.srt-row-muted {
  color: #A79FAE;
  font-size: 11px;
}

/* META ROW */

.srt-tc-meta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 16px;

  padding-top: 8px;

  border-top: 1px dashed #EEEAF2;
}

/* SUMMARY */

.srt-tc-summary {
  overflow: hidden;

  color: #7C7583;
  font-size: 11px;
  line-height: 1.5;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* SKILLS — full set, wraps freely */

.srt-row-skills-full {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.srt-row-skills-full span {
  display: inline-flex;
  align-items: center;

  padding: 3px 8px;

  color: var(--primary);
  background: #F5F0FC;
  border: 1px solid #E8DDF5;
  border-radius: 6px;

  font-size: 9.5px;
  font-weight: 650;
}

/* EXTRA ROW: certifications / languages / preferred locations */

.srt-tc-extra-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;

  padding-top: 6px;

  border-top: 1px dashed #EEEAF2;
}

.srt-tc-extra-item {
  display: inline-flex;
  align-items: flex-start;
  gap: 5px;

  max-width: 100%;

  color: #857E8C;
  font-size: 10px;
  font-weight: 550;
  line-height: 1.4;
}

.srt-tc-extra-item svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--secondary);
}

.srt-resume-status-sm {
  display: inline-flex;
  align-items: center;
  gap: 3px;

  flex: 0 0 auto;
  padding: 4px 8px;

  color: #857E8C;
  background: #F8F7FA;
  border: 1px solid #EAE6EE;
  border-radius: 20px;

  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
}

.srt-resume-status-sm.srt-active {
  color: var(--success);
  background: #F0FAF5;
  border-color: #D1EDDE;
}

.srt-row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex: 0 0 auto;
}

.srt-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 29px;
  height: 29px;
  padding: 0;
  flex-shrink: 0;

  border-radius: 7px;

  cursor: pointer;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;
}

.srt-icon-btn:active {
  transform: scale(0.94);
}

.srt-icon-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.srt-profile-icon-btn {
  color: var(--secondary);
  background: #F6F1FD;
  border: 1px solid #E3D4F6;
}

.srt-profile-icon-btn:hover:not(:disabled) {
  color: #FFFFFF;
  background: var(--secondary);
  border-color: var(--secondary);
}

.srt-resume-icon-btn {
  color: var(--primary);
  background: #F7F3FC;
  border: 1px solid #E1D5F1;
}

.srt-resume-icon-btn:hover:not(:disabled) {
  color: #FFFFFF;
  background: var(--primary);
  border-color: var(--primary);
}

.srt-request-icon-btn {
  color: #FFFFFF;
  background: var(--primary);
  border: 1px solid var(--primary);
}

.srt-request-icon-btn:hover:not(:disabled) {
  background: var(--secondary);
  border-color: var(--secondary);
}

/* =========================================================
   ERROR BOX
========================================================= */

.srt-error-box {
  display: flex;
  align-items: center;
  gap: 9px;

  padding: 10px 12px;
  margin-bottom: 12px;

  color: var(--danger);
  background: var(--danger-bg);

  border: 1px solid #F5CDD2;
  border-radius: 8px;
}

.srt-error-box > svg {
  flex-shrink: 0;
}

.srt-error-box > div {
  flex: 1;
  min-width: 0;
}

.srt-error-box strong {
  display: block;

  margin-bottom: 2px;

  font-size: 10px;
}

.srt-error-box span {
  display: block;

  color: #8C5D63;
  font-size: 9px;
}

.srt-error-box button {
  padding: 5px 9px;

  color: var(--danger);
  background: #FFFFFF;

  border: 1px solid #E7AEB5;
  border-radius: 6px;

  font-size: 9px;
  font-weight: 700;

  cursor: pointer;
}

.srt-error-box button:hover {
  color: #FFFFFF;
  background: var(--danger);
}

/* =========================================================
   LOADING / EMPTY STATES
========================================================= */

.srt-state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  min-height: 260px;
  padding: 28px;

  background: #FFFFFF;
  border: 1px solid var(--border);
  border-radius: 12px;

  text-align: center;
}

.srt-state-container > svg {
  color: var(--secondary);
}

.srt-state-container h3 {
  margin: 10px 0 4px;

  color: var(--text);
  font-size: 14px;
  font-weight: 750;
}

.srt-state-container p {
  margin: 0;

  color: #8A8491;
  font-size: 10px;
}

.srt-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;

  color: #8C8495;
  background: #F1EFF4;

  border-radius: 50%;
}

.srt-reset-empty-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  margin-top: 12px;
  padding: 7px 12px;

  color: #FFFFFF;
  background: var(--primary);

  border: none;
  border-radius: 7px;

  font-family: inherit;
  font-size: 9px;
  font-weight: 700;

  cursor: pointer;
}

.srt-reset-empty-btn:hover {
  background: var(--secondary);
}

/* =========================================================
   SPINNER
========================================================= */

.srt-spin {
  animation: srt-certed-spin 0.9s linear infinite;
}

@keyframes srt-certed-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* =========================================================
   MODAL OVERLAY
========================================================= */

.srt-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 16px;

  background: rgba(28, 22, 38, 0.62);

  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  animation: srt-modalFade 0.2s ease;
}

@keyframes srt-modalFade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* =========================================================
   RESUME MODAL
========================================================= */

.srt-resume-modal {
  display: flex;
  flex-direction: column;

  width: min(1050px, 96vw);
  height: min(88vh, 850px);

  overflow: hidden;

  background: #FFFFFF;

  border-radius: 12px;

  box-shadow: 0 20px 60px rgba(20, 15, 30, 0.3);

  animation: srt-modalScale 0.2s ease;
}

@keyframes srt-modalScale {
  from {
    opacity: 0;
    transform: scale(0.97) translateY(5px);
  }

  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.srt-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  padding: 12px 14px;

  background: #FFFFFF;

  border-bottom: 1px solid #E9E5ED;
}

.srt-modal-header > div {
  min-width: 0;
}

.srt-modal-header span {
  display: block;

  margin-bottom: 2px;

  color: var(--secondary);

  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.srt-modal-header h2 {
  margin: 0;

  overflow: hidden;

  color: var(--text);

  font-size: 14px;
  font-weight: 750;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.srt-modal-header button {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 30px;
  height: 30px;
  flex-shrink: 0;

  color: #6E6875;
  background: #F5F3F7;

  border: 1px solid #E7E3EA;
  border-radius: 7px;

  cursor: pointer;
  transition: 0.2s ease;
}

.srt-modal-header button:hover {
  color: #FFFFFF;
  background: var(--danger);
  border-color: var(--danger);
}

.srt-resume-frame {
  flex: 1;

  min-height: 0;

  background: #EDEBED;
}

.srt-resume-frame iframe {
  display: block;

  width: 100%;
  height: 100%;

  border: none;
}

/* =========================================================
   LIMIT MODAL
========================================================= */

.srt-limit-modal {
  position: relative;

  width: min(400px, 94vw);
  padding: 22px;

  background: #FFFFFF;

  border-radius: 15px;

  text-align: center;

  box-shadow: 0 20px 60px rgba(20, 15, 30, 0.25);

  animation: srt-modalScale 0.2s ease;
}

.srt-floating-close {
  position: absolute;
  top: 11px;
  right: 11px;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 27px;
  height: 27px;

  color: #77717E;
  background: #F6F4F8;

  border: none;
  border-radius: 50%;

  cursor: pointer;
}

.srt-floating-close:hover {
  color: var(--danger);
  background: #FFF0F1;
}

.srt-limit-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 50px;
  height: 50px;

  margin: 0 auto 10px;

  color: var(--primary);
  background: #F2EBFC;

  border-radius: 50%;
}

.srt-premium-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  padding: 4px 7px;
  margin-bottom: 7px;

  color: var(--secondary);
  background: #F7F0FE;

  border: 1px solid #EADDF8;
  border-radius: 20px;

  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.srt-limit-modal h2 {
  margin: 0 0 6px;

  color: var(--text);
  font-size: 17px;
  font-weight: 750;
}

.srt-limit-modal > p {
  max-width: 320px;

  margin: 0 auto 14px;

  color: #7E7785;
  font-size: 10px;
  line-height: 1.5;
}

/* LIMIT INFO */

.srt-limit-info {
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 10px;

  margin-bottom: 13px;

  background: #FAF9FC;
  border: 1px solid #ECE8F0;
  border-radius: 9px;
}

.srt-limit-info > div:not(.srt-limit-divider) {
  flex: 1;
  text-align: center;
}

.srt-limit-info strong {
  display: block;

  color: var(--primary);
  font-size: 15px;
  font-weight: 800;
}

.srt-limit-info span {
  display: block;

  margin-top: 2px;

  color: #8A8390;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.srt-limit-divider {
  width: 1px;
  height: 30px;

  background: #DDD8E2;
}

/* PLANS BUTTON */

.srt-plans-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  width: 100%;
  min-height: 38px;

  padding: 0 10px;

  color: #FFFFFF;
  background: var(--primary);

  border: none;
  border-radius: 8px;

  font-family: inherit;
  font-size: 10px;
  font-weight: 700;

  cursor: pointer;

  transition: 0.2s ease;
}

.srt-plans-btn:hover {
  background: var(--secondary);
}

.srt-plans-btn svg:last-child {
  margin-left: auto;
}

/* LATER BUTTON */

.srt-later-btn {
  width: 100%;

  margin-top: 6px;
  padding: 7px;

  color: #85808B;
  background: transparent;

  border: none;

  font-family: inherit;
  font-size: 9px;
  font-weight: 600;

  cursor: pointer;
}

.srt-later-btn:hover {
  color: var(--primary);
}

/* =========================================================
   PLANS MODAL
========================================================= */

.srt-plans-modal {
  display: flex;
  flex-direction: column;

  width: min(1050px, 96vw);
  max-height: 90vh;

  overflow: hidden;

  background: #FFFFFF;

  border-radius: 15px;

  box-shadow: 0 20px 60px rgba(20, 15, 30, 0.3);

  animation: srt-modalScale 0.2s ease;
}

.srt-plans-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 16px;

  padding: 16px 18px;

  border-bottom: 1px solid #ECE8F0;
}

.srt-plans-eyebrow {
  display: flex;
  align-items: center;
  gap: 5px;

  margin-bottom: 5px;

  color: var(--secondary);

  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.srt-plans-header h2 {
  margin: 0 0 4px;

  color: var(--text);

  font-size: 17px;
  font-weight: 750;
}

.srt-plans-header p {
  margin: 0;

  color: #837C8A;
  font-size: 9px;
}

.srt-plans-header > button {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 30px;
  height: 30px;
  flex-shrink: 0;

  color: #716B78;
  background: #F6F4F8;

  border: 1px solid #E8E4EB;
  border-radius: 7px;

  cursor: pointer;
}

.srt-plans-header > button:hover {
  color: #FFFFFF;
  background: var(--danger);
  border-color: var(--danger);
}

/* USAGE BOX */

.srt-usage-box {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin: 12px 18px;

  padding: 9px 12px;

  background: linear-gradient(135deg, #F8F3FD, #FCFAFE);

  border: 1px solid #E7DDF4;
  border-radius: 9px;
}

.srt-usage-box > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.srt-usage-box > div > svg {
  color: var(--secondary);
}

.srt-usage-box strong {
  color: var(--primary);
  font-size: 12px;
  font-weight: 750;
}

.srt-usage-box span {
  display: block;

  margin-top: 1px;

  color: #89828F;
  font-size: 8px;
}

/* PLANS CONTENT SCROLL */

.srt-plans-modal {
  overflow-y: auto;
}

.srt-plans-header,
.srt-usage-box {
  flex-shrink: 0;
}

.srt-plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));

  gap: 11px;

  padding: 0 18px 18px;
}

/* PLAN CARD */

.srt-plan-card {
  position: relative;

  display: flex;
  flex-direction: column;

  padding: 14px;

  background: #FFFFFF;

  border: 1px solid #E4DFE9;
  border-radius: 11px;

  transition: 0.2s ease;
}

.srt-plan-card:hover {
  transform: translateY(-2px);

  border-color: #CBB6E5;

  box-shadow: 0 8px 20px rgba(48, 42, 58, 0.08);
}

.srt-plan-card.srt-popular {
  border: 1.5px solid var(--secondary);

  box-shadow: 0 6px 20px rgba(149, 100, 221, 0.10);
}

.srt-popular-badge {
  position: absolute;
  top: -1px;
  right: 12px;

  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 4px 7px;

  color: #FFFFFF;
  background: var(--secondary);

  border-radius: 0 0 6px 6px;

  font-size: 8px;
  font-weight: 750;
}

.srt-plan-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 34px;
  height: 34px;

  margin-bottom: 9px;

  color: var(--primary);
  background: #F2ECFA;

  border-radius: 8px;
}

.srt-plan-card h3 {
  margin: 0 0 4px;

  color: var(--text);

  font-size: 13px;
  font-weight: 750;
}

.srt-plan-card > p {
  min-height: 26px;

  margin: 0 0 10px;

  color: #857E8B;

  font-size: 9px;
  line-height: 1.5;
}

.srt-plan-price {
  display: flex;
  align-items: baseline;

  margin-bottom: 6px;

  color: var(--primary);
}

.srt-plan-price span {
  font-size: 12px;
  font-weight: 700;
}

.srt-plan-price strong {
  margin-left: 2px;

  font-size: 21px;
  font-weight: 800;
}

.srt-plan-validity {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  width: fit-content;

  margin-bottom: 9px;
  padding: 4px 6px;

  color: #756D80;
  background: #F7F5F9;

  border-radius: 5px;

  font-size: 8px;
  font-weight: 600;
}

.srt-plan-validity svg {
  color: var(--secondary);
}

.srt-plan-resumes {
  display: flex;
  align-items: center;
  gap: 7px;

  margin-bottom: 9px;
  padding: 7px;

  color: var(--primary);
  background: #F8F4FC;

  border: 1px solid #E9DFF4;
  border-radius: 7px;
}

.srt-plan-resumes > svg {
  flex-shrink: 0;
  color: var(--secondary);
}

.srt-plan-resumes div {
  min-width: 0;
}

.srt-plan-resumes span {
  display: block;

  margin-bottom: 1px;

  color: #8A8290;
  font-size: 8px;
}

.srt-plan-resumes strong {
  display: block;

  color: var(--primary);
  font-size: 9px;
  font-weight: 750;
}

.srt-features {
  display: flex;
  flex-direction: column;
  gap: 5px;

  margin-bottom: 11px;
}

.srt-features > div {
  display: flex;
  align-items: flex-start;
  gap: 5px;

  color: #716A78;

  font-size: 9px;
  line-height: 1.35;
}

.srt-features svg {
  flex-shrink: 0;
  margin-top: 1px;

  color: var(--success);
}

.srt-purchase-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  width: 100%;
  min-height: 34px;

  margin-top: auto;

  color: #FFFFFF;
  background: var(--primary);

  border: none;
  border-radius: 7px;

  font-family: inherit;
  font-size: 9px;
  font-weight: 700;

  cursor: pointer;

  transition: 0.2s ease;
}

.srt-purchase-btn:hover:not(:disabled) {
  background: var(--secondary);
  box-shadow: 0 4px 12px rgba(149, 100, 221, 0.2);
}

.srt-purchase-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* PLANS STATE */

.srt-plans-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  min-height: 200px;

  padding: 24px 18px;

  text-align: center;
}

.srt-plans-state > svg {
  color: var(--secondary);
}

.srt-plans-state h3 {
  margin: 8px 0 4px;

  color: var(--text);

  font-size: 13px;
  font-weight: 750;
}

.srt-plans-state p {
  max-width: 340px;

  margin: 0 0 10px;

  color: #87808D;

  font-size: 9px;
  line-height: 1.45;
}

.srt-plans-state button {
  padding: 6px 11px;

  color: #FFFFFF;
  background: var(--primary);

  border: none;
  border-radius: 6px;

  font-family: inherit;
  font-size: 9px;
  font-weight: 700;

  cursor: pointer;
}

.srt-plans-state button:hover {
  background: var(--secondary);
}

/* =========================================================
   FULL PROFILE MODAL
========================================================= */

.srt-profile-modal {
  display: flex;
  flex-direction: column;

  width: min(720px, 96vw);
  max-height: 90vh;

  overflow: hidden;

  background: #FFFFFF;
  border-radius: 14px;

  box-shadow: 0 20px 60px rgba(20, 15, 30, 0.3);

  animation: srt-modalScale 0.2s ease;
}

.srt-profile-modal-header {
  flex-shrink: 0;
}

.srt-profile-modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  padding: 16px 18px;
}

.srt-profile-hero {
  display: flex;
  align-items: flex-start;
  gap: 13px;

  margin-bottom: 14px;
}

.srt-profile-avatar-lg {
  width: 62px;
  height: 62px;
  flex-shrink: 0;

  border-radius: 14px;
  object-fit: cover;
}

.srt-profile-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;

  color: #FFFFFF;
  background: linear-gradient(135deg, var(--primary), var(--secondary));

  font-size: 21px;
  font-weight: 750;
}

.srt-profile-hero-info {
  min-width: 0;
  flex: 1;
}

.srt-profile-title {
  color: var(--primary);
  font-size: 13px;
  font-weight: 750;
  margin-bottom: 3px;
}

.srt-profile-org {
  display: flex;
  align-items: center;
  gap: 5px;

  color: #756E7C;
  font-size: 10px;
  margin-bottom: 6px;
}

.srt-profile-org svg {
  color: var(--secondary);
  flex-shrink: 0;
}

.srt-profile-badges {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;

  margin-bottom: 7px;
}

.srt-profile-badges .srt-approved-label-sm {
  padding: 3px 7px;
  background: var(--success-bg);
  border: 1px solid #CBEBDD;
  border-radius: 20px;
}

.srt-teaching-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 3px 7px;

  color: var(--secondary);
  background: #F7F0FE;

  border: 1px solid #EADDF8;
  border-radius: 20px;

  font-size: 8px;
  font-weight: 700;
}

.srt-social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.srt-social-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 4px 8px;

  color: var(--primary);
  background: #F7F3FC;

  border: 1px solid #E1D5F1;
  border-radius: 6px;

  font-size: 9px;
  font-weight: 650;

  text-decoration: none;

  transition: 0.2s ease;
}

.srt-social-link:hover {
  color: #FFFFFF;
  background: var(--primary);
  border-color: var(--primary);
}

.srt-profile-meta-line {
  display: flex;
  align-items: center;
  gap: 4px;

  margin-top: 7px;

  color: #9992A0;
  font-size: 8.5px;
  font-weight: 550;
}

.srt-profile-meta-line svg {
  flex-shrink: 0;
  color: #B0A8BB;
}

.srt-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  margin-bottom: 14px;
}

.srt-stat-box {
  padding: 9px;

  text-align: center;

  background: #FAF9FC;
  border: 1px solid #ECE8F0;
  border-radius: 9px;
}

.srt-stat-box strong {
  display: block;

  color: var(--primary);
  font-size: 13px;
  font-weight: 800;
}

.srt-stat-box span {
  display: block;

  margin-top: 2px;

  color: #8A8390;
  font-size: 8px;
  font-weight: 650;
}

.srt-section-block {
  margin-bottom: 13px;
}

.srt-section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 13px;
}

.srt-section-label {
  display: flex;
  align-items: center;
  gap: 5px;

  margin-bottom: 6px;

  color: var(--text);
  font-size: 10px;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.srt-section-label svg {
  color: var(--secondary);
}

.srt-section-text {
  margin: 0;

  color: #625B6A;
  font-size: 11px;
  line-height: 1.55;
}

.srt-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.srt-chip-group span {
  padding: 3px 8px;

  color: var(--primary);
  background: #F5F0FC;

  border: 1px solid #E8DDF5;
  border-radius: 6px;

  font-size: 9px;
  font-weight: 650;
}

.srt-list-block {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.srt-list-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;

  color: #625B6A;
  font-size: 10.5px;
  line-height: 1.4;
}

.srt-list-item svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--success);
}

.srt-profile-modal-footer {
  display: flex;
  gap: 8px;

  padding: 11px 18px;
  flex-shrink: 0;

  border-top: 1px solid #ECE8F0;
}

.srt-profile-modal-footer button {
  flex: 1;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  height: 34px;

  border-radius: 7px;

  font-family: inherit;
  font-size: 10px;
  font-weight: 700;

  cursor: pointer;
}

.srt-profile-modal-footer .srt-resume-btn {
  color: var(--primary);
  background: #F7F3FC;
  border: 1px solid #E1D5F1;
}

.srt-profile-modal-footer .srt-resume-btn:hover:not(:disabled) {
  color: #FFFFFF;
  background: var(--primary);
  border-color: var(--primary);
}

.srt-profile-modal-footer .srt-request-btn {
  color: #FFFFFF;
  background: var(--primary);
  border: 1px solid var(--primary);
}

.srt-profile-modal-footer .srt-request-btn:hover:not(:disabled) {
  background: var(--secondary);
  border-color: var(--secondary);
}

/* =========================================================
   SCROLLBAR
========================================================= */

.srt-certed-trainer-page ::-webkit-scrollbar,
.srt-plans-modal::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.srt-certed-trainer-page ::-webkit-scrollbar-track,
.srt-plans-modal::-webkit-scrollbar-track {
  background: transparent;
}

.srt-certed-trainer-page ::-webkit-scrollbar-thumb,
.srt-plans-modal::-webkit-scrollbar-thumb {
  background: #D2CCD9;
  border-radius: 10px;
}

.srt-certed-trainer-page ::-webkit-scrollbar-thumb:hover,
.srt-plans-modal::-webkit-scrollbar-thumb:hover {
  background: #AAA2B3;
}

/* =========================================================
   TABLET
========================================================= */

@media (max-width: 1100px) {
  .srt-certed-trainer-page {
    padding: 14px;
  }

  .srt-marketplace-layout {
    grid-template-columns: 205px minmax(0, 1fr);
    gap: 12px;
  }

  .srt-header-stat {
    min-width: 115px;
  }
}

/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 850px) {
  .srt-certed-trainer-page {
    padding: 10px;
  }

  .srt-certed-header {
    align-items: flex-start;
    flex-direction: column;

    padding: 13px;
  }

  .srt-header-stats {
    width: 100%;
  }

  .srt-header-stat {
    flex: 1;
  }

  .srt-marketplace-layout {
    display: block;
  }

  .srt-filter-sidebar {
    position: static;

    margin-bottom: 12px;
  }

  .srt-filter-header {
    margin-bottom: 10px;
  }

  .srt-filter-group {
    margin-bottom: 10px;
  }

  .srt-mobile-clear {
    display: inline-flex;
    align-items: center;
    gap: 5px;

    padding: 6px 9px;

    color: var(--primary);
    background: #F6F1FC;

    border: 1px solid #E5D9F2;
    border-radius: 6px;

    font-family: inherit;
    font-size: 9px;
    font-weight: 700;

    cursor: pointer;
  }

  .srt-tc-top {
    flex-wrap: wrap;
  }

  .srt-tc-title-block {
    flex-basis: 100%;
  }
}

/* =========================================================
   SMALL MOBILE
========================================================= */

@media (max-width: 600px) {
  .srt-certed-trainer-page {
    padding: 8px;
  }

  .srt-certed-header {
    padding: 12px;
    border-radius: 10px;
  }

  .srt-certed-eyebrow {
    font-size: 8px;
  }

  .srt-certed-header h1 {
    font-size: 18px;
  }

  .srt-certed-header p {
    font-size: 10px;
  }

  .srt-header-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .srt-header-stat {
    min-width: 0;
    padding: 7px;
  }

  .srt-stat-icon {
    width: 25px;
    height: 25px;
  }

  .srt-header-stat strong {
    font-size: 12px;
  }

  .srt-header-stat span {
    font-size: 7px;
  }

  .srt-results-toolbar {
    align-items: flex-start;
  }

  .srt-results-toolbar h2 {
    font-size: 14px;
  }

  .srt-resume-modal {
    width: 96vw;
    height: 88vh;
    border-radius: 10px;
  }

  .srt-limit-modal {
    padding: 20px 15px;
  }

  .srt-plans-modal {
    width: 96vw;
    max-height: 92vh;
    border-radius: 12px;
  }

  .srt-profile-modal {
    width: 96vw;
    max-height: 92vh;
    border-radius: 12px;
  }

  .srt-profile-modal-body {
    padding: 13px;
  }

  .srt-profile-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .srt-plans-header {
    padding: 13px;
  }

  .srt-plans-header h2 {
    font-size: 15px;
  }

  .srt-usage-box {
    margin: 10px 13px;
  }

  .srt-plans-grid {
    grid-template-columns: 1fr;

    padding: 0 13px 13px;
  }
}

/* =========================================================
   VERY SMALL DEVICES
========================================================= */

@media (max-width: 400px) {
  .srt-header-stats {
    grid-template-columns: 1fr;
  }

  .srt-results-toolbar {
    flex-direction: column;
    gap: 8px;
  }

  .srt-mobile-clear {
    align-self: flex-start;
  }

  .srt-limit-info {
    padding: 10px;
  }
}
`}</style>
    </>
  );
}
export default SearchTrainers;